import React, { useState, useEffect } from 'react';
import { Row, Col } from 'reactstrap';
import { withStyles } from '@material-ui/core';
import Tooltip from '@mui/material/Tooltip';
import * as Constants from '../../Pages/Constants';
import { FormattedMessage } from 'react-intl';
import Utils from '../Common/Utils.js';
import PatientFunctionsGetter from '../../Controller/getSQLPatientFunctions.js';
import DatePeriod from '../DateRange/DatePeriod.js';
import GetDataController from '../../Controller/getPatientGenericData.js';
import TuckyDataManager from '../Tucky/TuckyDataManager.js';
import PulseOximeterManager from '../PulseOximeter/pulseOximeterManager.js';
import TensionManager from '../Tensiometer/TensiometerManager.js';
import ScaleManager from '../Scale/ScaleManager.js';
import PainManager from '../Pain/PainManager.js';
import BreathingManager from '../Niv/BreathingManager.js';
import HealthFormManager from '../Healthform/HealthformManager.js';
import DataServiceSQL from '../../DataService/DataServiceSQL.js';
import CopyableId from '../Common/CopyableId.js';
import useScreenSize from '../Common/useScreenSize.js';
import { userIsSuperAdmin, userIsInPsadStructure } from '../../Controller/userRoles.js';
import { NON_MEDICAL_ALERT_TYPES, computeAlertAckDisabler, computeCanValidateTls, shouldShowTls } from './alertPermissions.js';

// [i18n key, French fallback] per alert type. Resolved through the caller's
// translate helper so the labels follow the account language.
const alertTypeLabels = {
    fever_alert: ['patient.profile.alertType.fever', "Fièvre"],
    PR_alert: ['patient.profile.alertType.oximeterHeartRate', "Fréquence cardiaque (oxymètre)"],
    SPO2_alert: ['patient.profile.alertType.spo2', "SpO2"],
    bloodpressure_PR_alert: ['patient.profile.alertType.tensiometerHeartRate', "Fréquence cardiaque (tensiomètre)"],
    bloodpressure_sys_high_alert: ['patient.profile.alertType.systolic', "Pression systolique"],
    bloodpressure_dia_alert: ['patient.profile.alertType.diastolic', "Pression diastolique"],
    weight_alert: ['patient.profile.alertType.weight', "Poids"],
    eq5d_alert: ['patient.profile.alertType.eq5d', "EQ5D"],
    eq5d_scale_alert: ['patient.profile.alertType.eq5dScale', "EQ5D échelle"],
    sf36_alert: ['patient.profile.alertType.sf36', "SF36"],
    mdasi_alert: ['patient.profile.alertType.mdasi', "MDASI"],
    pain_alert: ['patient.profile.alertType.pain', "Douleur"],
    breathing_rate_alert: ['patient.profile.alertType.breathingRate', "Fréquence respiratoire"],
    breathing_oxygen_rate_alert: ['patient.profile.alertType.oxygenFlow', "Débit oxygène"],
    breathing_leakage_alert: ['patient.profile.alertType.leak', "Fuite"],
    breathing_observance_alert: ['patient.profile.alertType.compliance', "Observance"],
    reminder_alert: ['patient.profile.alertType.reminder', "Rappel"],
    detach_alert: ['patient.profile.alertType.detach', "Détachement"],
    battery_alert: ['patient.profile.alertType.battery', "Batterie"],
    pos_alert: ['patient.profile.alertType.position', "Position"],
};

const alertTypeLabel = (translate, type) => {
    const entry = alertTypeLabels[type];
    return entry ? translate(entry[0], entry[1]) : type;
};

const formatAlertDate = (dateStr, locale) => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    return d.toLocaleDateString(locale || 'fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const isCommentLockedByAcknowledgement = (comment, acknowledgementDate) => {
    if (!comment?.created_at || !acknowledgementDate) return false;
    const commentTime = new Date(comment.created_at).getTime();
    const acknowledgementTime = new Date(acknowledgementDate).getTime();
    return Number.isFinite(commentTime)
        && Number.isFinite(acknowledgementTime)
        && commentTime <= acknowledgementTime;
};

const alertActionOptions = [
    { value: '', key: null, label: '-' },
    { value: 'appel_patient', key: 'patient.profile.alertAction.callPatient', label: 'Appel patient' },
    { value: 'appel_prescripteur', key: 'patient.profile.alertAction.callPrescriber', label: 'Appel prescripteur' },
    { value: 'element_isole', key: 'patient.profile.alertAction.isolatedEvent', label: 'Élément isolé' },
];

const alertActionLabel = (translate, option) => (option.key ? translate(option.key, option.label) : option.label);

const AlertTable = ({ alerts, title, emptyMessage, intl, alertConfigs, onAck, pendingAckId, onActionChange, alertActions, readOnly, disableAck, commentCounts, commentPreviews, onOpenComments, currentUserId, onEditFirstComment, noNewComments, compact }) => {
    const th = intl ? (id, def) => intl.formatMessage({ id, defaultMessage: def }) : (id, def) => def;
    const dateLocale = intl ? intl.locale : 'fr-FR';
    const defaultPageSize = compact ? 3 : 5;
    const [page, setPage] = React.useState(0);
    const [pageSize, setPageSize] = React.useState(defaultPageSize);
    const [expandedPreviewId, setExpandedPreviewId] = React.useState(null);
    React.useEffect(() => {
        setPageSize(defaultPageSize);
        setPage(0);
    }, [defaultPageSize]);
    const getThreshold = (alertType) => {
        if (!alertConfigs || !alertConfigs[alertType]) return '-';
        const conf = alertConfigs[alertType];
        return conf.threshold != null ? conf.threshold : '-';
    };
    const totalPages = Math.ceil(alerts.length / pageSize);
    const pagedAlerts = alerts.slice(page * pageSize, (page + 1) * pageSize);
    const pageSizeOptions = compact ? [3, 10, 30, 50] : [5, 10, 30, 50];
    const renderActionCell = (a, showLabel = false) => (
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            {showLabel && (
                <span style={{ fontSize: 12, color: '#888' }}>
                    {th('patient.profile.alert.action', 'Action')} :
                </span>
            )}
            {readOnly
                ? <span style={{ fontSize: 12 }}>{(() => { const opt = alertActionOptions.find(o => o.value === (a.action || '')); return opt ? alertActionLabel(th, opt) : '-'; })()}</span>
                : <select
                    value={(alertActions && alertActions[a.identifier]) || a.action || ''}
                    onChange={(e) => onActionChange && onActionChange(a.identifier, e.target.value)}
                    style={{ fontSize: 12, padding: '2px 4px', borderRadius: 3, border: '1px solid #ccc', cursor: 'pointer' }}
                  >
                      {alertActionOptions.map(opt => (
                          <option key={opt.value} value={opt.value}>{alertActionLabel(th, opt)}</option>
                      ))}
                  </select>
            }
        </div>
    );
    const renderCommentsCell = (a, conversationMode = false) => (
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6, flexWrap: conversationMode ? 'wrap' : 'nowrap' }}>
            <div style={{ flex: 1, minWidth: 0 }}>
                {commentPreviews && commentPreviews[a.identifier] ? (() => {
                    const preview = commentPreviews[a.identifier];
                    const isOwner = currentUserId && preview.FK_User === currentUserId;
                    const isLocked = isCommentLockedByAcknowledgement(preview, a.received);
                    const canEdit = isOwner && !noNewComments && !isLocked;
                    const msg = preview.message || '';
                    const isExpanded = expandedPreviewId === a.identifier;
                    const needsTruncate = msg.length > 200;
                    const displayMsg = (!needsTruncate || isExpanded) ? msg : msg.substring(0, 200) + '...';
                    return (
                        <div>
                            <div
                                onClick={canEdit ? () => { onEditFirstComment && onEditFirstComment(a.identifier); } : undefined}
                                style={{
                                    fontSize: 12,
                                    color: '#45505f',
                                    marginBottom: 4,
                                    lineHeight: '1.4',
                                    cursor: canEdit ? 'pointer' : 'default',
                                    borderRadius: conversationMode ? '8px 8px 8px 2px' : 3,
                                    padding: conversationMode ? '6px 8px' : '2px 4px',
                                    backgroundColor: conversationMode ? '#fff' : 'transparent',
                                    border: conversationMode ? '1px solid #d9e5f2' : 'none',
                                }}
                                title={isLocked
                                    ? th('patient.profile.alert.commentLocked', 'Verrouillé après acknowledgement')
                                    : canEdit ? th('patient.profile.alert.editComment', 'Cliquer pour modifier') : ''}
                            >
                                {displayMsg}
                                {canEdit && <span style={{ marginLeft: 6, color: '#1976d2', fontSize: 11 }}>✎</span>}
                                {isLocked && <span style={{ marginLeft: 6, color: '#78909c', fontSize: 11 }} aria-label={th('patient.profile.alert.commentLocked', 'Verrouillé après acknowledgement')}>🔒</span>}
                            </div>
                            {needsTruncate && (
                                <div style={{ textAlign: 'center', marginTop: 2 }}>
                                    <button
                                        onClick={() => {
                                            if (msg.length >= 600) {
                                                onOpenComments && onOpenComments(a.identifier);
                                            } else {
                                                setExpandedPreviewId(isExpanded ? null : a.identifier);
                                            }
                                        }}
                                        style={{ background: 'none', border: '1px solid #ccc', borderRadius: '50%', width: 20, height: 20, cursor: 'pointer', fontSize: 14, lineHeight: '16px', color: '#1976d2', padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
                                        title={isExpanded ? th('patient.profile.alert.collapseComment', 'Réduire') : th('patient.profile.alert.expandComment', 'Voir plus')}
                                    >{isExpanded ? '−' : '+'}</button>
                                </div>
                            )}
                        </div>
                    );
                })() : (
                    noNewComments ? null : (
                    <button
                        onClick={() => onOpenComments && onOpenComments(a.identifier)}
                        style={{ background: 'linear-gradient(135deg, #e3f2fd 0%, #f3e5f5 100%)', border: '1px dashed #90caf9', borderRadius: 6, cursor: 'pointer', padding: '6px 12px', fontSize: 12, color: '#1976d2', display: 'inline-flex', alignItems: 'center', gap: 6, fontWeight: 500, transition: 'all 0.2s' }}
                        title={th('patient.profile.alert.addComment', 'Ajouter un commentaire')}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
                        </svg>
                        {th('patient.profile.alert.addComment', 'Ajouter un commentaire')}
                    </button>
                    )
                )}
            </div>
            {!conversationMode && commentCounts && commentCounts[a.identifier] >= 1 && (
            <button
                onClick={() => onOpenComments && onOpenComments(a.identifier)}
                style={{ background: 'none', border: '1px solid #ccc', borderRadius: 4, cursor: 'pointer', padding: '2px 8px', fontSize: 12, color: '#1976d2', display: 'inline-flex', alignItems: 'center', gap: 4, flexShrink: 0, alignSelf: 'flex-start' }}
                title={th('patient.profile.alert.openComments', 'Voir les commentaires')}
            >
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                </svg>
                {commentCounts[a.identifier] === 1
                    ? <span>+ {th('patient.profile.alert.addShort', 'Add')}</span>
                    : <span style={{ fontWeight: 'bold' }}>{commentCounts[a.identifier] - 1}</span>
                }
            </button>
            )}
        </div>
    );
    const renderConversationSection = (a) => (
        <div
            className="alert-conversation-section"
            role="group"
            aria-label={th('patient.profile.alert.conversation', 'Conversation')}
            style={{
                margin: '10px -10px -8px',
                padding: '8px 10px 10px',
                backgroundColor: '#f3f8ff',
                borderTop: '1px solid #c9dcf2',
                borderRadius: '0 0 6px 6px',
            }}
        >
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 7, color: '#1976d2' }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M21 15a4 4 0 0 1-4 4H8l-5 3V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4z"/>
                    <line x1="8" y1="9" x2="16" y2="9"/>
                    <line x1="8" y1="13" x2="13" y2="13"/>
                </svg>
                <span style={{ fontSize: 12, fontWeight: 600 }}>
                    {th('patient.profile.alert.conversation', 'Conversation')}
                </span>
                {commentCounts && commentCounts[a.identifier] > 0 && (
                    <span style={{ minWidth: 18, height: 18, padding: '0 5px', borderRadius: 9, backgroundColor: '#1976d2', color: '#fff', fontSize: 11, fontWeight: 600, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                        {commentCounts[a.identifier]}
                    </span>
                )}
            </div>
            {renderCommentsCell(a, true)}
            {commentCounts && commentCounts[a.identifier] > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginTop: 7, flexWrap: 'wrap' }}>
                    {!noNewComments && (
                        <button
                            onClick={() => onOpenComments && onOpenComments(a.identifier)}
                            style={{ backgroundColor: '#1976d2', border: '1px solid #1976d2', borderRadius: 6, cursor: 'pointer', padding: '5px 9px', fontSize: 12, color: '#fff', display: 'inline-flex', alignItems: 'center', gap: 5, fontWeight: 500 }}
                            title={th('patient.profile.alert.addComment', 'Ajouter un commentaire')}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
                            </svg>
                            {th('patient.profile.alert.addComment', 'Ajouter un commentaire')}
                        </button>
                    )}
                    <button
                        onClick={() => onOpenComments && onOpenComments(a.identifier)}
                        style={{ backgroundColor: '#fff', border: '1px solid #90b9e8', borderRadius: 6, cursor: 'pointer', padding: '5px 9px', fontSize: 12, color: '#1976d2', display: 'inline-flex', alignItems: 'center', gap: 5, fontWeight: 500 }}
                        title={th('patient.profile.alert.openConversation', 'Voir la conversation')}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                        </svg>
                        {th('patient.profile.alert.openConversation', 'Voir la conversation')}
                    </button>
                </div>
            )}
        </div>
    );
    const renderValidationCell = (a, isAckDisabled) => (
        a.received
            ? <span style={{ color: '#21BA45', fontWeight: 'bold' }}>{formatAlertDate(a.received, dateLocale)}</span>
            : <input
                type="checkbox"
                style={{ width: 18, height: 18, cursor: isAckDisabled ? 'not-allowed' : 'pointer', opacity: isAckDisabled ? 0.4 : 1 }}
                title={isAckDisabled ? th('patient.profile.alert.ackDelegated', 'Alertes non médicales déléguées au PSAD — validation réservée à la structure PSAD') : th('patient.profile.alert.ack', 'Valider la prise en charge')}
                checked={pendingAckId === a.identifier}
                onChange={() => !isAckDisabled && onAck && onAck(a.identifier)}
                disabled={isAckDisabled}
              />
    );
    return (
    <div style={{ marginBottom: 16 }}>
        {title && <h4 style={{ margin: '12px 0 8px', color: '#333' }}>{title}</h4>}
        {alerts.length === 0 ? (
            <p style={{ color: '#999', fontStyle: 'italic' }}>{emptyMessage}</p>
        ) : (
            <>
            {compact ? (
                <div>
                    {pagedAlerts.map((a, i) => {
                        const isAckDisabled = typeof disableAck === 'function' ? disableAck(a.note[0]) : !!disableAck;
                        return (
                            <div key={i} className="alert-card" style={{ border: '1px solid #d8e0e8', borderRadius: 6, padding: '8px 10px', marginBottom: 10, backgroundColor: '#fff', overflow: 'hidden' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 4 }}>
                                    <span style={{ fontWeight: 600, fontSize: 13 }}>{alertTypeLabel(th, a.note[0])}</span>
                                    <span style={{ fontSize: 12, color: '#888' }}>{formatAlertDate(a.sent, dateLocale)}</span>
                                </div>
                                <div style={{ display: 'flex', gap: 16, fontSize: 12, marginTop: 4, flexWrap: 'wrap' }}>
                                    <span><span style={{ color: '#888' }}>{th('patient.profile.alert.threshold', 'Seuil')} :</span> <strong>{getThreshold(a.note[0])}</strong></span>
                                    <span><span style={{ color: '#888' }}>{th('patient.profile.alert.value', 'Détecté')} :</span> <strong>{a.note[1] !== undefined && a.note[1] !== null ? a.note[1] : '-'}</strong></span>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 6, flexWrap: 'wrap' }}>
                                    {renderActionCell(a, true)}
                                    <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6 }}>
                                        <span style={{ fontSize: 12, color: '#888' }}>{th('patient.profile.alert.validation', 'Validation')} :</span>
                                        {renderValidationCell(a, isAckDisabled)}
                                    </div>
                                </div>
                                {renderConversationSection(a)}
                            </div>
                        );
                    })}
                </div>
            ) : (
            <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, minWidth: 700 }}>
                <thead>
                    <tr style={{ backgroundColor: '#f5f5f5', borderBottom: '2px solid #ddd' }}>
                        <th style={{ padding: '6px 8px', textAlign: 'left', whiteSpace: 'nowrap' }}>{th('patient.profile.alert.constant', 'Constante')}</th>
                        <th style={{ padding: '6px 8px', textAlign: 'left', whiteSpace: 'nowrap' }}>{th('patient.profile.alert.datetime', 'Date et heure')}</th>
                        <th style={{ padding: '6px 8px', textAlign: 'left', whiteSpace: 'nowrap' }}>{th('patient.profile.alert.threshold', 'Seuil')}</th>
                        <th style={{ padding: '6px 8px', textAlign: 'left', whiteSpace: 'nowrap' }}>{th('patient.profile.alert.value', 'Détecté')}</th>
                        <th style={{ padding: '6px 8px', textAlign: 'left', whiteSpace: 'nowrap' }}>{th('patient.profile.alert.actions', 'Actions')}</th>
                        <th style={{ padding: '6px 8px', textAlign: 'left', whiteSpace: 'nowrap' }}>{th('patient.profile.alert.comments', 'Commentaires')}</th>
                        <th style={{ padding: '6px 8px', textAlign: 'center', lineHeight: 1.3 }}>{th('patient.profile.alert.validation', 'Validation')}</th>
                    </tr>
                </thead>
                <tbody>
                    {pagedAlerts.map((a, i) => {
                        const isAckDisabled = typeof disableAck === 'function' ? disableAck(a.note[0]) : !!disableAck;
                        return (
                        <tr key={i} style={{ borderBottom: '1px solid #eee' }}>
                            <td style={{ padding: '5px 6px', whiteSpace: 'nowrap' }}>{alertTypeLabel(th, a.note[0])}</td>
                            <td style={{ padding: '5px 6px', whiteSpace: 'nowrap' }}>{formatAlertDate(a.sent, dateLocale)}</td>
                            <td style={{ padding: '5px 6px' }}>{getThreshold(a.note[0])}</td>
                            <td style={{ padding: '5px 6px' }}>{a.note[1] !== undefined && a.note[1] !== null ? a.note[1] : '-'}</td>
                            <td style={{ padding: '5px 6px' }}>{renderActionCell(a)}</td>
                            <td style={{ padding: '5px 6px' }}>{renderCommentsCell(a)}</td>
                            <td style={{ padding: '5px 6px', textAlign: 'center' }}>{renderValidationCell(a, isAckDisabled)}</td>
                        </tr>
                        );
                    })}
                </tbody>
            </table>
            </div>
            )}
            {alerts.length > defaultPageSize && (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8, flexWrap: 'wrap', gap: 8 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#666', flexWrap: 'wrap' }}>
                        <span>{th('patient.profile.alert.perPage', 'Par page')} :</span>
                        {pageSizeOptions.map(size => (
                            <button
                                key={size}
                                onClick={() => { setPageSize(size); setPage(0); }}
                                style={{ padding: '2px 8px', cursor: 'pointer', border: '1px solid #ccc', borderRadius: 3, backgroundColor: pageSize === size ? '#1976d2' : '#fff', color: pageSize === size ? '#fff' : '#333', fontWeight: pageSize === size ? 'bold' : 'normal', fontSize: 12 }}
                            >
                                {size}
                            </button>
                        ))}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <button
                            onClick={() => setPage(p => Math.max(0, p - 1))}
                            disabled={page === 0}
                            style={{ padding: '4px 12px', cursor: page === 0 ? 'default' : 'pointer', opacity: page === 0 ? 0.4 : 1, border: '1px solid #ccc', borderRadius: 3, backgroundColor: '#fff' }}
                        >←</button>
                        <span style={{ fontSize: 13, color: '#666' }}>{page + 1} / {totalPages}</span>
                        <button
                            onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                            disabled={page >= totalPages - 1}
                            style={{ padding: '4px 12px', cursor: page >= totalPages - 1 ? 'default' : 'pointer', opacity: page >= totalPages - 1 ? 0.4 : 1, border: '1px solid #ccc', borderRadius: 3, backgroundColor: '#fff' }}
                        >→</button>
                    </div>
                </div>
            )}
            </>
        )}
    </div>
    );
};

const PatientProfileModal = (props) => {
    const { classes, patientData, cancelPopup, subscription_info, dateSelected, intl, accountInformation, struct_info, onCheckPatientUpdate, onAlertAck } = props;
    const pd = patientData;
    const t = (id, def) => intl ? intl.formatMessage({ id, defaultMessage: def }) : def;
    const dateLocale = intl ? intl.locale : 'fr-FR';

    const [graphsExpanded, setGraphsExpanded] = useState(false);
    const [graphDateSelected, setGraphDateSelected] = useState(dateSelected);
    const [graphPd, setGraphPd] = useState(null);
    const [zoomedGraph, setZoomedGraph] = useState(null);
    const [questionnairesExpanded, setQuestionnairesExpanded] = useState(false);
    const [synthesisExpanded, setSynthesisExpanded] = useState(true);
    const [expandedQuestionnaires, setExpandedQuestionnaires] = useState(new Set());
    const [practitioner, setPractitioner] = useState(null);
    const [ackedAlerts, setAckedAlerts] = useState({});
    const [alertActions, setAlertActions] = useState({});
    const [pendingAckId, setPendingAckId] = useState(null);
    const [showAckConfirm, setShowAckConfirm] = useState(false);
    // Keep the wider desktop table as the default. Users can opt into the
    // denser two-column overview with the layout toggle.
    const [twoColLayout, setTwoColLayout] = useState(false);
    // Responsive: 1280px and below (mobile + tablet + small laptop) the modal is forced to single-column;
    // useScreenSize tracks resize/orientation changes, unlike a one-shot window.innerWidth read
    const screenSize = useScreenSize();
    const isMobile = screenSize.width < 768;
    const isCompact = screenSize.width <= 1280;
    const effectiveTwoCol = twoColLayout && !isCompact;
    // Alert cards are used on mobile/tablet and whenever each alert section
    // only has half of the modal width. A wide desktop single column keeps the table.
    const isAlertCompact = isCompact || effectiveTwoCol;
    const [commentCounts, setCommentCounts] = useState({});
    const [commentPreviews, setCommentPreviews] = useState({});
    const [commentAlertId, setCommentAlertId] = useState(null);
    const [comments, setComments] = useState([]);
    const [commentText, setCommentText] = useState('');
    const [editingCommentId, setEditingCommentId] = useState(null);
    const [editingText, setEditingText] = useState('');
    const [loadingComments, setLoadingComments] = useState(false);

    // PSAD structure name resolution
    const [psadStructureName, setPsadStructureName] = useState(null);
    const [psadShareStruct, setPsadShareStruct] = useState(null);

    // TLS validation
    const isPsadUser = userIsInPsadStructure(accountInformation);
    const showTls = shouldShowTls(
        accountInformation,
        props.tlsSubscriptionFeatures ?? subscription_info?.features
    );
    const canValidateTls = computeCanValidateTls(accountInformation, pd);
    const monitoringActive = !!(pd.monitoring_activation && pd.monitoring_activation !== 'no');
    const practitionerHasDelegatedAlerts = computeAlertAckDisabler(isPsadUser, pd.psad_delegate, pd.delegate_non_medical_alert);
    const [checkPatientData, setCheckPatientData] = useState(pd.check_patient || []);
    const [showTlsConfirm, setShowTlsConfirm] = useState(false);

    const handleTlsValidation = async () => {
        setShowTlsConfirm(false);
        if (!monitoringActive) return;
        try {
            const id_user = accountInformation?.id_user || null;
            const user_name = accountInformation ? (accountInformation.firstName + ' ' + accountInformation.lastName) : null;
            const structure_name = struct_info?.struct_name || null;
            const newEntry = { date: new Date().toISOString(), id_user, user_name, structure_name };
            const updated = [newEntry, ...(checkPatientData || [])];
            setCheckPatientData(updated);
            await PatientFunctionsGetter.update_patient_check(pd.id_patient, id_user, user_name, structure_name);
            if (onCheckPatientUpdate) {
                onCheckPatientUpdate(pd.id_patient, updated);
            }
        } catch (e) {
            console.error('Error validating TLS:', e);
        }
    };

    // Past alerts filters
    const [pastAlertTypeFilter, setPastAlertTypeFilter] = useState(new Set());
    const [pastAlertDateFrom, setPastAlertDateFrom] = useState('');
    const [pastAlertDateTo, setPastAlertDateTo] = useState('');

    const getAlertAcknowledgement = (idAlert) => {
        if (ackedAlerts[idAlert]) return ackedAlerts[idAlert];
        const alert = Array.isArray(pd.alerts_triggered)
            ? pd.alerts_triggered.find(item => String(item.identifier) === String(idAlert))
            : null;
        return alert?.received || null;
    };

    const isLockedComment = (comment, idAlert = commentAlertId) => (
        isCommentLockedByAcknowledgement(comment, getAlertAcknowledgement(idAlert))
    );

    const openComments = async (idAlert) => {
        setCommentAlertId(idAlert);
        setCommentText('');
        setEditingCommentId(null);
        setLoadingComments(true);
        try {
            const res = await DataServiceSQL.getAlertComments(idAlert);
            setComments(res.data || []);
        } catch (e) {
            console.error('Error loading comments:', e);
            setComments([]);
        } finally {
            setLoadingComments(false);
        }
    };

    const openCommentsAndEditFirst = async (idAlert) => {
        setCommentAlertId(idAlert);
        setCommentText('');
        setEditingCommentId(null);
        setLoadingComments(true);
        try {
            const res = await DataServiceSQL.getAlertComments(idAlert);
            const loadedComments = res.data || [];
            setComments(loadedComments);
            if (loadedComments.length > 0
                && loadedComments[0].FK_User === accountInformation?.id_user
                && !isLockedComment(loadedComments[0], idAlert)) {
                setEditingCommentId(loadedComments[0].id_comment);
                setEditingText(loadedComments[0].message);
            }
        } catch (e) {
            console.error('Error loading comments:', e);
            setComments([]);
        } finally {
            setLoadingComments(false);
        }
    };

    const closeComments = () => {
        setCommentAlertId(null);
        setComments([]);
        setCommentText('');
        setEditingCommentId(null);
    };

    const submitComment = async () => {
        if (!commentText.trim() || !accountInformation?.id_user) return;
        try {
            await DataServiceSQL.createAlertComment(commentAlertId, accountInformation.id_user, commentText.trim());
            setCommentText('');
            const res = await DataServiceSQL.getAlertComments(commentAlertId);
            setComments(res.data || []);
            setCommentCounts(prev => ({ ...prev, [commentAlertId]: (res.data || []).length }));
            if ((res.data || []).length > 0) setCommentPreviews(prev => ({ ...prev, [commentAlertId]: res.data[0] }));
            else setCommentPreviews(prev => { const n = { ...prev }; delete n[commentAlertId]; return n; });
        } catch (e) {
            console.error('Error creating comment:', e);
        }
    };

    const startEditComment = (comment) => {
        if (isLockedComment(comment)) return;
        setEditingCommentId(comment.id_comment);
        setEditingText(comment.message);
    };

    const cancelEditComment = () => {
        setEditingCommentId(null);
        setEditingText('');
    };

    const saveEditComment = async (id_comment) => {
        if (!editingText.trim() || !accountInformation?.id_user) return;
        const comment = comments.find(item => item.id_comment === id_comment);
        if (!comment || isLockedComment(comment)) return;
        try {
            await DataServiceSQL.updateAlertComment(commentAlertId, id_comment, accountInformation.id_user, editingText.trim());
            setEditingCommentId(null);
            setEditingText('');
            const res = await DataServiceSQL.getAlertComments(commentAlertId);
            setComments(res.data || []);
            if ((res.data || []).length > 0) setCommentPreviews(prev => ({ ...prev, [commentAlertId]: res.data[0] }));
        } catch (e) {
            console.error('Error updating comment:', e);
        }
    };

    const deleteComment = async (id_comment) => {
        if (!accountInformation?.id_user) return;
        const comment = comments.find(item => item.id_comment === id_comment);
        if (!comment || isLockedComment(comment)) return;
        try {
            await DataServiceSQL.deleteAlertComment(commentAlertId, id_comment, accountInformation.id_user);
            const res = await DataServiceSQL.getAlertComments(commentAlertId);
            setComments(res.data || []);
            setCommentCounts(prev => ({ ...prev, [commentAlertId]: (res.data || []).length }));
            if ((res.data || []).length > 0) setCommentPreviews(prev => ({ ...prev, [commentAlertId]: res.data[0] }));
            else setCommentPreviews(prev => { const n = { ...prev }; delete n[commentAlertId]; return n; });
        } catch (e) {
            console.error('Error deleting comment:', e);
        }
    };

    const inlineEditPreview = async (idAlert, idComment, newMessage) => {
        if (!accountInformation?.id_user) return;
        const preview = commentPreviews[idAlert];
        if (preview?.id_comment === idComment && isLockedComment(preview, idAlert)) return;
        try {
            await DataServiceSQL.updateAlertComment(idAlert, idComment, accountInformation.id_user, newMessage);
            const res = await DataServiceSQL.getAlertComments(idAlert);
            const data = res.data || [];
            if (data.length > 0) setCommentPreviews(prev => ({ ...prev, [idAlert]: data[0] }));
        } catch (e) {
            console.error('Error inline editing comment:', e);
        }
    };

    const requestAckAlert = (idAlert) => {
        setPendingAckId(idAlert);
        setShowAckConfirm(true);
    };

    const cancelAck = () => {
        setPendingAckId(null);
        setShowAckConfirm(false);
    };

    const confirmAckAlert = async () => {
        setShowAckConfirm(false);
        try {
            // Save pending action before acknowledging
            const pendingAction = alertActions[pendingAckId];
            if (pendingAction) {
                await DataServiceSQL.updateAlertAction(pendingAckId, pendingAction);
            }
            await DataServiceSQL.validateAlert(pendingAckId);
            const ackTime = new Date().toISOString();
            setAckedAlerts(prev => ({ ...prev, [pendingAckId]: ackTime }));
            if (onAlertAck) onAlertAck(pd.id_patient, pendingAckId, ackTime);
        } catch (e) {
            console.error('Error acknowledging alert:', e);
        } finally {
            setPendingAckId(null);
        }
    };

    const handleActionChange = async (idAlert, action) => {
        try {
            await DataServiceSQL.updateAlertAction(idAlert, action);
            setAlertActions(prev => ({ ...prev, [idAlert]: action }));
        } catch (e) {
            console.error('Error updating alert action:', e);
        }
    };

    useEffect(() => {
        const rpps = pd.rpps_id_practioner_speciality;
        if (rpps) {
            PatientFunctionsGetter.search_practitioners({ rpps }).then(results => {
                setPractitioner(results && results.length > 0 ? results[0] : null);
            });
        } else {
            setPractitioner(null);
        }
    }, [pd.id_patient, pd.rpps_id_practioner_speciality]);

    useEffect(() => {
        let cancelled = false;
        if (!pd.psad_delegate) {
            setPsadShareStruct(null);
            setPsadStructureName(null);
            return;
        }
        (async () => {
            // Always resolve the structure pointed to by psad_delegate (the assigned
            // delegate), not just the first PSAD share (which is the patient's own structure).
            const shares = await PatientFunctionsGetter.get_patient_structure_shares(pd.id_patient);
            let resolved = (shares || []).find(s => String(s.id_structure) === String(pd.psad_delegate)) || null;
            if (!resolved) {
                const structures = await PatientFunctionsGetter.get_structures_by_type('psad');
                resolved = (structures || []).find(s => String(s.id_structure) === String(pd.psad_delegate)) || null;
            }
            if (!cancelled) {
                setPsadShareStruct(resolved);
                setPsadStructureName(resolved ? resolved.structure_name : null);
            }
        })();
        return () => { cancelled = true; };
    }, [pd.id_patient, pd.psad_delegate]);
    const [questionnairePage, setQuestionnairePage] = useState({});
    const [expandedResponseIdx, setExpandedResponseIdx] = useState(null);

    // Alerts — merge locally acked alerts
    const allAlerts = (pd.alerts_triggered && Array.isArray(pd.alerts_triggered))
        ? pd.alerts_triggered.map(a => {
            const localAction = alertActions[a.identifier];
            const merged = localAction !== undefined ? { ...a, action: localAction } : a;
            return ackedAlerts[merged.identifier] ? { ...merged, received: ackedAlerts[merged.identifier] } : merged;
        })
        : [];
    const activeAlerts = allAlerts.filter(a => !a.received);
    const passedAlerts = allAlerts.filter(a => !!a.received);
    // Sort by date descending
    const sortedActiveAlerts = [...activeAlerts].sort((a, b) => new Date(b.sent) - new Date(a.sent));
    const sortedPassedAlerts = (() => {
        let filtered = [...passedAlerts];
        if (pastAlertTypeFilter.size > 0) {
            filtered = filtered.filter(a => pastAlertTypeFilter.has(a.note[0]));
        }
        if (pastAlertDateFrom) {
            const from = new Date(pastAlertDateFrom);
            from.setHours(0, 0, 0, 0);
            filtered = filtered.filter(a => new Date(a.sent) >= from);
        }
        if (pastAlertDateTo) {
            const to = new Date(pastAlertDateTo);
            to.setHours(23, 59, 59, 999);
            filtered = filtered.filter(a => new Date(a.sent) <= to);
        }
        filtered.sort((a, b) => {
            const acknowledgementDelta = new Date(b.received) - new Date(a.received);
            return acknowledgementDelta || new Date(b.sent) - new Date(a.sent);
        });
        return filtered;
    })();
    const pastAlertTypes = [...new Set(passedAlerts.map(a => a.note[0]))].sort();

    const isModuleFollowed = (moduleName) => (
        Array.isArray(pd.module_activation)
        && pd.module_activation.some(module => module.name === moduleName && module.used === true)
    );
    const isDeviceFollowed = (deviceKey) => {
        const device = pd.devices && pd.devices[deviceKey];
        return !!(device && device.id && device.id !== 'dissociate');
    };
    const tuckyIsFollowed = isDeviceFollowed('device1') || isModuleFollowed('tucky_smartphone') || pd.monitoring_activation === 'smartphone';
    const hasData = (data) => Array.isArray(data) && data.length > 0;
    const hasAnyData = (...dataSets) => dataSets.some(hasData);
    const getOximeterSpO2Data = (patient) => {
        const sp02Data = patient.pulseOximeter?.sp02;
        if (hasData(sp02Data)) return sp02Data;
        return patient.pulseOximeter?.SPO2 || [];
    };

    // Load comment counts and first comment preview for all alerts on mount
    useEffect(() => {
        const loadCounts = async () => {
            const counts = {};
            const previews = {};
            const alertIds = allAlerts.map(a => a.identifier);
            for (const id of alertIds) {
                try {
                    const res = await DataServiceSQL.getAlertComments(id);
                    const data = res.data || [];
                    counts[id] = data.length;
                    if (data.length > 0) previews[id] = data[0];
                } catch (e) {
                    counts[id] = 0;
                }
            }
            setCommentCounts(counts);
            setCommentPreviews(previews);
        };
        if (allAlerts.length > 0) loadCounts();
    }, [allAlerts.length]);

    // Compute monitoring start date (oldest data point across all sensor data)
    const monitoringStartDate = (() => {
        const dates = [];
        const pushFirst = (arr) => { if (arr && arr.length > 0) dates.push(new Date(arr[0].x).getTime()); };
        pushFirst(pd.thermometer);
        pushFirst(pd.pain);
        pushFirst(pd.pulseOximeter?.pulseRate);
        pushFirst(getOximeterSpO2Data(pd));
        pushFirst(pd.pulseTensiometer?.pulseRate);
        pushFirst(pd.pulseTensiometer?.systolic);
        pushFirst(pd.scale_data?.weight);
        pushFirst(pd.breathing?.niv_usage);
        pushFirst(pd.breathing?.oxi_usage);
        pushFirst(pd.breathing?.respiratory_rate);
        pushFirst(pd.breathing?.oxygen_flow_rate);
        pushFirst(pd.eq5d?.form_res);
        pushFirst(pd.sf36?.form_res);
        pushFirst(pd.mdasi?.form_res);
        pushFirst(pd.alimentation?.form_res);
        return dates.length > 0 ? new Date(Math.min(...dates)) : null;
    })();

    // Monitoring synthesis: per-sensor alert stats
    const monitoringSynthesis = (() => {
        const rows = [];
        const feat = (f) => Utils.checkBit(subscription_info.features, Utils.featureToBitNumber(f));
        const computeRow = (label, alertTypes, isFollowed, hasCurrentData) => {
            const relevant = allAlerts.filter(a => alertTypes.includes(a.note[0]));
            if ((!isFollowed || !hasCurrentData) && relevant.length === 0) return;
            const lastAlert = relevant.length > 0
                ? [...relevant].sort((a, b) => new Date(b.sent) - new Date(a.sent))[0].sent
                : null;
            let avgExceedance = null;
            if (relevant.length > 0 && pd.alerts) {
                const pcts = [];
                relevant.forEach(a => {
                    const conf = pd.alerts[a.note[0]];
                    if (conf && conf.threshold != null && a.note[1] != null) {
                        const th = parseFloat(conf.threshold);
                        const val = parseFloat(a.note[1]);
                        if (th !== 0) {
                            const pct = conf.sup_or_inf === 'inf'
                                ? ((th - val) / th) * 100
                                : ((val - th) / th) * 100;
                            if (pct > 0) pcts.push(pct);
                        }
                    }
                });
                if (pcts.length > 0) avgExceedance = pcts.reduce((s, v) => s + v, 0) / pcts.length;
            }
            rows.push({ label, totalAlerts: relevant.length, lastAlert, avgExceedance });
        };
        const oximeterIsFollowed = feat('oximeter') && isDeviceFollowed('device2');
        const tensiometerIsFollowed = feat('tensiometer') && isDeviceFollowed('device3');
        const nivIsFollowed = feat('niv') && isModuleFollowed('niv');
        const oxygenTherapyIsFollowed = feat('oxigen_therapy') && isModuleFollowed('oxigen_therapy');

        computeRow(t('patient.profile.synthesis.row.temperature', 'Température'), ['fever_alert'], feat('thermometer') && tuckyIsFollowed, hasData(pd.thermometer));
        computeRow(t('patient.profile.synthesis.row.spo2', 'SpO2'), ['SPO2_alert'], oximeterIsFollowed, hasData(getOximeterSpO2Data(pd)));
        computeRow(t('patient.profile.synthesis.row.oximeterHeartRate', 'FC (oxymètre)'), ['PR_alert'], oximeterIsFollowed, hasData(pd.pulseOximeter?.pulseRate));
        computeRow(t('patient.profile.synthesis.row.systolic', 'Pression systolique'), ['bloodpressure_sys_high_alert'], tensiometerIsFollowed, hasData(pd.pulseTensiometer?.systolic));
        computeRow(t('patient.profile.synthesis.row.diastolic', 'Pression diastolique'), ['bloodpressure_dia_alert'], tensiometerIsFollowed, hasData(pd.pulseTensiometer?.diastolic));
        computeRow(t('patient.profile.synthesis.row.tensiometerHeartRate', 'FC (tensiomètre)'), ['bloodpressure_PR_alert'], tensiometerIsFollowed, hasData(pd.pulseTensiometer?.pulseRate));
        computeRow(t('patient.profile.synthesis.row.weight', 'Poids'), ['weight_alert'], feat('scale') && isDeviceFollowed('device4'), hasData(pd.scale_data?.weight));
        computeRow(t('patient.profile.synthesis.row.pain', 'Douleur'), ['pain_alert'], feat('pain') && isModuleFollowed('pain'), hasData(pd.pain));
        computeRow(t('patient.profile.synthesis.row.breathingRate', 'Fréq. respiratoire'), ['breathing_rate_alert'], nivIsFollowed, hasData(pd.breathing?.respiratory_rate));
        computeRow(t('patient.profile.synthesis.row.nivLeak', 'Fuite VNI'), ['breathing_leakage_alert'], nivIsFollowed, hasData(pd.breathing?.respiratory_leak));
        computeRow(t('patient.profile.synthesis.row.nivCompliance', 'Observance VNI'), ['breathing_observance_alert'], nivIsFollowed, hasData(pd.breathing?.niv_usage));
        computeRow(t('patient.profile.synthesis.row.oxygenFlow', 'Débit O₂'), ['breathing_oxygen_rate_alert'], oxygenTherapyIsFollowed, hasData(pd.breathing?.oxygen_flow_rate));
        computeRow('EQ5D', ['eq5d_alert', 'eq5d_scale_alert'], feat('healthform_EQ5D') && isModuleFollowed('EQ5D'), hasAnyData(pd.eq5d?.form_res, pd.eq5d?.healthscale));
        computeRow('SF36', ['sf36_alert'], feat('healthform_SF36') && isModuleFollowed('SF36'), hasData(pd.sf36?.form_res));
        computeRow('MDASI', ['mdasi_alert'], feat('healthform_MDASI') && isModuleFollowed('MDASI'), hasData(pd.mdasi?.form_res));
        return rows;
    })();

    // Build graph list based on available data
    // Use unique num suffixes (300+) to avoid canvas ID collisions with graphs already on the page
    // (same pattern as commit f975ca64 using num=200 for GraphPopup)
    const effectivePd = graphPd || pd;
    const graphProps = {
        dateSelected: graphDateSelected,
        intl: intl,
        profileData: { Patient_Data: effectivePd },
        accountInformation: {},
        pointRadius: 4
    };

    const handleGraphDateChange = async (dateTab) => {
        const dates = dateTab.map(d => new Date(d.getFullYear(), d.getMonth(), d.getDate(), d.getHours(), d.getMinutes(), d.getSeconds(), d.getMilliseconds()).toISOString());
        setGraphDateSelected(dates);

        const startDate = new Date(dates[0]);
        const endDate = new Date(dates[1]);
        const fhirCodes = Utils.getFHIRCodesFromFeatures(subscription_info.features);

        try {
            const all_data = await GetDataController.get_patient_generic_Data(pd.id_patient, startDate, endDate, fhirCodes);
            const newPd = { ...pd };

            // Temperature
            const temp_data = Utils.filterData(all_data, "8310-5");
            newPd.thermometer = TuckyDataManager.formatAndMergeTemperatureData(true, [], temp_data);

            // Pulse Oximeter
            const spo2_data = Utils.filterData(all_data, "2708-6");
            newPd.pulseOximeter = PulseOximeterManager.formatAndMergeOximeterData(true, pd.pulseOximeter || { sp02: [], pulseRate: [] }, spo2_data);

            // Tensiometer
            const bp_data = Utils.filterData(all_data, "8460-6");
            newPd.pulseTensiometer = TensionManager.formatAndMergeTensiometerData(true, pd.pulseTensiometer || { systolic: [], diastolic: [], pulseRate: [] }, bp_data);

            // Scale
            let scale_data = Utils.filterData(all_data, "29463-7");
            scale_data = Utils.filterScaleData(scale_data);
            newPd.scale_data = ScaleManager.formatAndMergeScaleData(true, pd.scale_data || { weight: [] }, scale_data);

            // Pain
            const pain_data = Utils.filterData(all_data, "72514-3");
            newPd.pain = PainManager.formatAndMergePainData([], pain_data);

            // Breathing
            const niv_schedule = Utils.filterData(all_data, "69988-4");
            const oxi_schedule = Utils.filterData(all_data, "LP75328-2");
            const resp_rate = Utils.filterData(all_data, "9279-1");
            const resp_leak = Utils.filterData(all_data, "33456-5");
            const oxy_flow = Utils.filterData(all_data, "3151-8");
            newPd.breathing = BreathingManager.formatAndMergeBreathingData(
                pd.breathing || { niv_usage: [], oxi_usage: [], respiratory_rate: [], respiratory_leak: [], oxygen_flow_rate: [] },
                niv_schedule, oxi_schedule, resp_rate, resp_leak, oxy_flow, endDate, 0
            );

            // Health forms
            const hf_data = Utils.filterData(all_data, "45698-1");
            const eq5d_data = Utils.filterHealthform(hf_data, "EQ5D5L");
            newPd.eq5d = HealthFormManager.formatAndMergeEQ5DData(pd.eq5d || { form_res: [], healthscale: [] }, eq5d_data);
            const sf36_data = Utils.filterHealthform(hf_data, "SF36");
            newPd.sf36 = HealthFormManager.formatAndMergeSF36Data(pd.sf36 || { form_res: [] }, sf36_data);
            const mdasi_data = Utils.filterHealthform(hf_data, "MDASI");
            newPd.mdasi = HealthFormManager.formatAndMergeMDASIData(pd.mdasi || { form_res: [] }, mdasi_data);
            const alim_data = Utils.filterHealthform(hf_data, "ALIMENTATION");
            newPd.alimentation = HealthFormManager.formatAndMergeAlimentationData(pd.alimentation || { form_res: [] }, alim_data);

            // Alert curves
            if (newPd.alerts) {
                newPd.MaxTemperatureDrawing = Utils.setAlertCurve(startDate, endDate, newPd.alerts.fever_alert?.threshold);
                newPd.MaxPulseRateDrawing = Utils.setAlertCurve(startDate, endDate, newPd.alerts.PR_alert?.threshold);
                newPd.MinSpO2Drawing = Utils.setAlertCurve(startDate, endDate, newPd.alerts.SPO2_alert?.threshold);
                newPd.MaxPulseRateDrawingTension = Utils.setAlertCurve(startDate, endDate, newPd.alerts.bloodpressure_PR_alert?.threshold);
                newPd.MaxSystolicDrawingTension = Utils.setAlertCurve(startDate, endDate, newPd.alerts.bloodpressure_sys_high_alert?.threshold);
                newPd.MaxDiastolicDrawingTension = Utils.setAlertCurve(startDate, endDate, newPd.alerts.bloodpressure_dia_alert?.threshold);
                newPd.weightThreshold = Utils.setAlertCurve(startDate, endDate, newPd.alerts.weight_alert?.threshold);
                newPd.MaxPainDrawing = Utils.setAlertCurve(startDate, endDate, newPd.alerts.pain_alert?.threshold);
                newPd.MaxBreathingRateDrawing = Utils.setAlertCurve(startDate, endDate, newPd.alerts.breathing_rate_alert?.threshold);
                newPd.MaxBreathingOxygenRateDrawing = Utils.setAlertCurve(startDate, endDate, newPd.alerts.breathing_oxygen_rate_alert?.threshold);
                newPd.MaxBreathingLeakageDrawing = Utils.setAlertCurve(startDate, endDate, newPd.alerts.breathing_leakage_alert?.threshold);
                newPd.MaxBreathingObservanceDrawing = Utils.setAlertCurve(startDate, endDate, newPd.alerts.breathing_observance_alert?.threshold);
                newPd.MaxHealthformDrawing = Utils.setAlertCurve(startDate, endDate, newPd.alerts.eq5d_alert?.threshold);
                newPd.MaxHealthscaleDrawing = Utils.setAlertCurve(startDate, endDate, newPd.alerts.eq5d_scale_alert?.threshold);
            }

            setGraphPd(newPd);
        } catch (e) {
            console.error('Error fetching graph data for new date range:', e);
        }
    };

    const availableGraphs = [];
    const gp = (pr) => ({ ...graphProps, pointRadius: pr || graphProps.pointRadius });
    if (Utils.checkBit(subscription_info.features, Utils.featureToBitNumber("thermometer")) && effectivePd.thermometer && effectivePd.thermometer.length > 0) {
        availableGraphs.push({ key: 'temperature', label: t('patient.profile.graph.temperature', 'Température'), render: (pr) => Utils.display_temperatureGraph(gp(pr), false) });
    }
    if (Utils.checkBit(subscription_info.features, Utils.featureToBitNumber("oximeter")) && effectivePd.pulseOximeter && (hasData(effectivePd.pulseOximeter.pulseRate) || hasData(getOximeterSpO2Data(effectivePd)))) {
        availableGraphs.push({ key: 'oximeter', label: t('patient.profile.graph.oximeter', 'Oxymètre (FC + SpO2)'), render: (pr) => Utils.display_pulseOximeterGraph(gp(pr), 300, false) });
    }
    if (Utils.checkBit(subscription_info.features, Utils.featureToBitNumber("tensiometer")) && effectivePd.pulseTensiometer && (hasData(effectivePd.pulseTensiometer.systolic) || hasData(effectivePd.pulseTensiometer.diastolic) || hasData(effectivePd.pulseTensiometer.pulseRate))) {
        availableGraphs.push({ key: 'tensiometer', label: t('patient.profile.graph.tensiometer', 'Tensiomètre'), render: (pr) => Utils.display_tensiometerGraph(gp(pr), false, 301) });
    }
    if (Utils.checkBit(subscription_info.features, Utils.featureToBitNumber("scale")) && effectivePd.scale_data && effectivePd.scale_data.weight && effectivePd.scale_data.weight.length > 0) {
        availableGraphs.push({ key: 'scale', label: t('patient.profile.graph.weight', 'Poids'), render: (pr) => Utils.display_scaleGraph(gp(pr), false, 302) });
    }
    if (Utils.checkBit(subscription_info.features, Utils.featureToBitNumber("pain")) && effectivePd.pain && effectivePd.pain.length > 0) {
        availableGraphs.push({ key: 'pain', label: t('patient.profile.graph.pain', 'Douleur'), render: (pr) => Utils.display_painGraph(gp(pr), false) });
    }
    if (Utils.checkBit(subscription_info.features, Utils.featureToBitNumber("niv")) && effectivePd.breathing) {
        if (effectivePd.breathing.niv_usage && effectivePd.breathing.niv_usage.length > 0) {
            availableGraphs.push({ key: 'niv_usage', label: t('patient.profile.graph.nivUsage', 'VNI (usage)'), render: (pr) => Utils.display_breathingGraph_NIVUsage(gp(pr), false) });
        }
        if (effectivePd.breathing.respiratory_leak && effectivePd.breathing.respiratory_leak.length > 0) {
            availableGraphs.push({ key: 'niv_leak', label: t('patient.profile.graph.nivLeak', 'VNI (fuite)'), render: (pr) => Utils.display_breathingGraph_NIVLeakage(gp(pr), false) });
        }
        if (effectivePd.breathing.respiratory_rate && effectivePd.breathing.respiratory_rate.length > 0) {
            availableGraphs.push({ key: 'niv_rate', label: t('patient.profile.graph.breathingRate', 'Fréquence respiratoire'), render: (pr) => Utils.display_breathingGraph_NIVBreathingRate(gp(pr), false) });
        }
    }
    if (Utils.checkBit(subscription_info.features, Utils.featureToBitNumber("oxigen_therapy")) && effectivePd.breathing) {
        if (effectivePd.breathing.oxi_usage && effectivePd.breathing.oxi_usage.length > 0) {
            availableGraphs.push({ key: 'oxi_usage', label: t('patient.profile.graph.oxiUsage', 'Oxygénothérapie (usage)'), render: (pr) => Utils.display_breathingGraph_OxiUsage(gp(pr), false) });
        }
        if (effectivePd.breathing.oxygen_flow_rate && effectivePd.breathing.oxygen_flow_rate.length > 0) {
            availableGraphs.push({ key: 'oxi_rate', label: t('patient.profile.graph.oxygenFlow', 'Débit oxygène'), render: (pr) => Utils.display_breathingGraph_OxygenFlowRate(gp(pr), false) });
        }
    }

    // Questionnaire items (interactive: click title → paginated response list → click response → show answers)
    const availableQuestionnaires = [];
    if (Utils.checkBit(subscription_info.features, Utils.featureToBitNumber("healthform_EQ5D")) && pd.eq5d && pd.eq5d.form_res && pd.eq5d.form_res.length > 0) {
        availableQuestionnaires.push({ key: 'eq5d', label: 'EQ5D' });
    }
    if (Utils.checkBit(subscription_info.features, Utils.featureToBitNumber("healthform_SF36")) && pd.sf36 && pd.sf36.form_res && pd.sf36.form_res.length > 0) {
        availableQuestionnaires.push({ key: 'sf36', label: 'SF36' });
    }
    if (Utils.checkBit(subscription_info.features, Utils.featureToBitNumber("healthform_MDASI")) && pd.mdasi && pd.mdasi.form_res && pd.mdasi.form_res.length > 0) {
        availableQuestionnaires.push({ key: 'mdasi', label: 'MDASI' });
    }
    if (Utils.checkBit(subscription_info.features, Utils.featureToBitNumber("healthform_ALIMENTATION")) && pd.alimentation && pd.alimentation.form_res && pd.alimentation.form_res.length > 0) {
        availableQuestionnaires.push({ key: 'alimentation', label: t('patient.profile.questionnaires.alimentation', 'Alimentation') });
    }

    // --- Questionnaire interaction helpers ---
    const ITEMS_PER_PAGE = 5;

    const toggleQuestionnaire = (key) => {
        setExpandedQuestionnaires(prev => {
            const next = new Set(prev);
            if (next.has(key)) {
                next.delete(key);
            } else {
                next.add(key);
                if (questionnairePage[key] === undefined) {
                    setQuestionnairePage(p => ({ ...p, [key]: 0 }));
                }
            }
            return next;
        });
        setExpandedResponseIdx(null);
    };

    const getFormRes = (key) => {
        let data = [];
        switch (key) {
            case 'eq5d': data = pd.eq5d?.form_res || []; break;
            case 'sf36': data = pd.sf36?.form_res || []; break;
            case 'mdasi': data = pd.mdasi?.form_res || []; break;
            case 'alimentation': data = pd.alimentation?.form_res || []; break;
            default: break;
        }
        return [...data].sort((a, b) => new Date(b.x) - new Date(a.x));
    };

    const formatQDate = (timestamp) => {
        const d = new Date(timestamp);
        return d.toLocaleDateString(dateLocale, { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    };

    const convertYesNoMaybe = (val) => {
        const v = String(val);
        if (v === '1') return t('patient.profile.yes', 'Oui');
        if (v === '0') return t('patient.profile.no', 'Non');
        return '-';
    };

    const qLabels = {
        sf36: {
            one: t('patient.profile.q.sf36.one', "État de santé général"),
            two: t('patient.profile.q.sf36.two', "Évolution santé vs. il y a 1 an"),
            three_one: t('patient.profile.q.sf36.three_one', "Activités intenses (courir, sport)"),
            three_two: t('patient.profile.q.sf36.three_two', "Activités modérées (table, aspirateur)"),
            three_three: t('patient.profile.q.sf36.three_three', "Soulever courses"),
            three_four: t('patient.profile.q.sf36.three_four', "Monter plusieurs étages"),
            three_five: t('patient.profile.q.sf36.three_five', "Monter un étage"),
            three_six: t('patient.profile.q.sf36.three_six', "Se pencher, s'agenouiller"),
            three_seven: t('patient.profile.q.sf36.three_seven', "Marcher plus d'1 km"),
            three_eight: t('patient.profile.q.sf36.three_eight', "Marcher plusieurs centaines de m"),
            three_nine: t('patient.profile.q.sf36.three_nine', "Marcher 100 mètres"),
            three_ten: t('patient.profile.q.sf36.three_ten', "Se laver, s'habiller"),
            four_one: t('patient.profile.q.sf36.four_one', "Réduire temps travail (physique)"),
            four_two: t('patient.profile.q.sf36.four_two', "Accompli moins (physique)"),
            four_three: t('patient.profile.q.sf36.four_three', "Limité dans le type d'activités"),
            four_four: t('patient.profile.q.sf36.four_four', "Difficulté à exécuter le travail"),
            five_one: t('patient.profile.q.sf36.five_one', "Réduire temps travail (émotionnel)"),
            five_two: t('patient.profile.q.sf36.five_two', "Accompli moins (émotionnel)"),
            five_three: t('patient.profile.q.sf36.five_three', "Travail moins soigné"),
            six: t('patient.profile.q.sf36.six', "Interférence sociale"),
            seven: t('patient.profile.q.sf36.seven', "Douleurs physiques"),
            eight: t('patient.profile.q.sf36.eight', "Interférence de la douleur"),
            nine_one: t('patient.profile.q.sf36.nine_one', "Plein(e) de vie"),
            nine_two: t('patient.profile.q.sf36.nine_two', "Très nerveux/nerveuse"),
            nine_three: t('patient.profile.q.sf36.nine_three', "Si triste que rien ne console"),
            nine_four: t('patient.profile.q.sf36.nine_four', "Calme et serein(e)"),
            nine_five: t('patient.profile.q.sf36.nine_five', "Beaucoup d'énergie"),
            nine_six: t('patient.profile.q.sf36.nine_six', "Triste et abattu(e)"),
            nine_seven: t('patient.profile.q.sf36.nine_seven', "Épuisé(e)"),
            nine_eight: t('patient.profile.q.sf36.nine_eight', "Heureux/heureuse"),
            nine_nine: t('patient.profile.q.sf36.nine_nine', "Fatigué(e)"),
            ten: t('patient.profile.q.sf36.ten', "Interférence sociale (fréquence)"),
            eleven_one: t('patient.profile.q.sf36.eleven_one', "Tombe malade facilement"),
            eleven_two: t('patient.profile.q.sf36.eleven_two', "Aussi bonne santé que les autres"),
            eleven_three: t('patient.profile.q.sf36.eleven_three', "Santé va se dégrader"),
            eleven_four: t('patient.profile.q.sf36.eleven_four', "Excellente santé"),
        },
        mdasi: {
            severity_one: t('patient.profile.q.mdasi.severity_one', "Douleur la plus intense"),
            severity_two: t('patient.profile.q.mdasi.severity_two', "Fatigue la plus intense"),
            severity_three: t('patient.profile.q.mdasi.severity_three', "Nausées les plus intenses"),
            severity_four: t('patient.profile.q.mdasi.severity_four', "Troubles du sommeil"),
            severity_five: t('patient.profile.q.mdasi.severity_five', "Niveau d'inquiétude"),
            severity_six: t('patient.profile.q.mdasi.severity_six', "Essoufflement le plus intense"),
            severity_seven: t('patient.profile.q.mdasi.severity_seven', "Problèmes de mémoire"),
            severity_height: t('patient.profile.q.mdasi.severity_height', "Perte d'appétit"),
            severity_nine: t('patient.profile.q.mdasi.severity_nine', "Somnolence"),
            severity_ten: t('patient.profile.q.mdasi.severity_ten', "Sécheresse buccale"),
            severity_eleven: t('patient.profile.q.mdasi.severity_eleven', "Niveau de tristesse"),
            severity_twelve: t('patient.profile.q.mdasi.severity_twelve', "Vomissements"),
            severity_thirteen: t('patient.profile.q.mdasi.severity_thirteen', "Engourdissements / picotements"),
            impact_one: t('patient.profile.q.mdasi.impact_one', "Activité générale"),
            impact_two: t('patient.profile.q.mdasi.impact_two', "Humeur"),
            impact_three: t('patient.profile.q.mdasi.impact_three', "Travail"),
            impact_four: t('patient.profile.q.mdasi.impact_four', "Relations"),
            impact_five: t('patient.profile.q.mdasi.impact_five', "Marche"),
            impact_six: t('patient.profile.q.mdasi.impact_six', "Goût de vivre"),
        },
        alimentation: {
            transit_one: t('patient.profile.q.alim.transit_one', "Flatulences"),
            transit_two: t('patient.profile.q.alim.transit_two', "Selles"),
            transit_three: t('patient.profile.q.alim.transit_three', "Nausées et vomissements"),
            pain_one: t('patient.profile.q.alim.pain_one', "Efficacité du traitement"),
            pain_two: t('patient.profile.q.alim.pain_two', "Besoin d'aide traitement"),
            alimentation_one_0: t('patient.profile.q.alim.alimentation_one_0', "Eau / boissons"),
            alimentation_one_1: t('patient.profile.q.alim.alimentation_one_1', "Yaourt / compote"),
            alimentation_one_2: t('patient.profile.q.alim.alimentation_one_2', "Alimentation légère"),
            alimentation_one_3: t('patient.profile.q.alim.alimentation_one_3', "Alimentation quasi normale"),
            alimentation_two: t('patient.profile.q.alim.alimentation_two', "Appétit en hausse"),
            alimentation_three: t('patient.profile.q.alim.alimentation_three', "Besoin d'aide alimentation"),
        },
    };

    const sf36Opts = {
        one: [t('patient.profile.q.sf36opt.one.0', "Excellente"), t('patient.profile.q.sf36opt.one.1', "Très bonne"), t('patient.profile.q.sf36opt.one.2', "Bonne"), t('patient.profile.q.sf36opt.one.3', "Passable"), t('patient.profile.q.sf36opt.one.4', "Mauvaise")],
        two: [t('patient.profile.q.sf36opt.two.0', "Bien meilleure"), t('patient.profile.q.sf36opt.two.1', "À peu près pareil"), t('patient.profile.q.sf36opt.two.2', "Un peu meilleure"), t('patient.profile.q.sf36opt.two.3', "Un peu moins bonne"), t('patient.profile.q.sf36opt.two.4', "Bien moins bonne")],
        three: [t('patient.profile.q.sf36opt.three.0', "Oui, très limité(e)"), t('patient.profile.q.sf36opt.three.1', "Oui, un peu limité(e)"), t('patient.profile.q.sf36opt.three.2', "Non, pas du tout limité(e)")],
        four: [t('patient.profile.q.sf36opt.four.0', "Oui"), t('patient.profile.q.sf36opt.four.1', "Non")],
        five: [t('patient.profile.q.sf36opt.five.0', "Oui"), t('patient.profile.q.sf36opt.five.1', "Non")],
        six: [t('patient.profile.q.sf36opt.six.0', "Pas du tout"), t('patient.profile.q.sf36opt.six.1', "Un peu"), t('patient.profile.q.sf36opt.six.2', "Modérément"), t('patient.profile.q.sf36opt.six.3', "Beaucoup")],
        seven: [t('patient.profile.q.sf36opt.seven.0', "Pas du tout"), t('patient.profile.q.sf36opt.seven.1', "Un peu"), t('patient.profile.q.sf36opt.seven.2', "Modérément"), t('patient.profile.q.sf36opt.seven.3', "Beaucoup")],
        eight: [t('patient.profile.q.sf36opt.eight.0', "Pas du tout"), t('patient.profile.q.sf36opt.eight.1', "Un petit peu"), t('patient.profile.q.sf36opt.eight.2', "Modérément"), t('patient.profile.q.sf36opt.eight.3', "Beaucoup"), t('patient.profile.q.sf36opt.eight.4', "Extrêmement")],
        nine: [t('patient.profile.q.sf36opt.nine.0', "Tout le temps"), t('patient.profile.q.sf36opt.nine.1', "La plupart du temps"), t('patient.profile.q.sf36opt.nine.2', "Parfois"), t('patient.profile.q.sf36opt.nine.3', "Rarement"), t('patient.profile.q.sf36opt.nine.4', "Jamais")],
        ten: [t('patient.profile.q.sf36opt.ten.0', "Tout le temps"), t('patient.profile.q.sf36opt.ten.1', "La plupart du temps"), t('patient.profile.q.sf36opt.ten.2', "Parfois"), t('patient.profile.q.sf36opt.ten.3', "Rarement"), t('patient.profile.q.sf36opt.ten.4', "Jamais")],
        eleven: [t('patient.profile.q.sf36opt.eleven.0', "Tout à fait vrai"), t('patient.profile.q.sf36opt.eleven.1', "Plutôt vrai"), t('patient.profile.q.sf36opt.eleven.2', "Ne sait pas"), t('patient.profile.q.sf36opt.eleven.3', "Plutôt faux"), t('patient.profile.q.sf36opt.eleven.4', "Tout à fait faux")],
    };

    const getSF36Group = (label) => {
        if (['one','two','six','seven','eight','ten'].includes(label)) return label;
        const prefix = label.split('_')[0];
        if (['three','four','five','nine','eleven'].includes(prefix)) return prefix;
        return null;
    };

    const dtStyle = { width: '100%', borderCollapse: 'collapse', fontSize: 13 };
    const dthStyle = { padding: '6px 10px', textAlign: 'left', backgroundColor: '#f5f5f5', borderBottom: '1px solid #ddd' };
    const dtdStyle = { padding: '5px 10px', borderBottom: '1px solid #eee' };

    const renderResponseDetail = (qKey, response) => {
        if (qKey === 'eq5d') {
            const healthScaleEntry = (pd.eq5d?.healthscale || []).find(h => h.x === response.x);
            return (
                <table style={dtStyle}>
                    <thead><tr><th style={dthStyle}>{t('patient.profile.questionnaires.measure', 'Mesure')}</th><th style={dthStyle}>{t('patient.profile.questionnaires.value', 'Valeur')}</th></tr></thead>
                    <tbody>
                        <tr><td style={dtdStyle}>{t('patient.profile.questionnaires.eqIndex', 'EQ Index')}</td><td style={dtdStyle}>{response.y}</td></tr>
                        <tr><td style={dtdStyle}>{t('patientConstants.healthScale', 'Échelle de santé')}</td><td style={dtdStyle}>{healthScaleEntry ? healthScaleEntry.y + ' / 100' : '-'}</td></tr>
                    </tbody>
                </table>
            );
        }
        let data;
        try { data = typeof response.y === 'string' ? JSON.parse(response.y) : response.y; }
        catch (e) { return <p style={{ color: '#999', fontStyle: 'italic' }}>{t('patient.profile.dataUnavailable', 'Données non disponibles')}</p>; }
        if (!Array.isArray(data)) return <p style={{ color: '#999', fontStyle: 'italic' }}>{t('patient.profile.dataUnavailable', 'Données non disponibles')}</p>;
        const answers = data.filter(d => d.label !== 'registered_by');
        const labels = qLabels[qKey] || {};

        if (qKey === 'sf36') {
            return (
                <table style={dtStyle}>
                    <thead><tr><th style={dthStyle}>{t('patient.profile.questionnaires.question', 'Question')}</th><th style={dthStyle}>{t('patient.profile.questionnaires.answer', 'Réponse')}</th></tr></thead>
                    <tbody>
                        {answers.map((a, i) => {
                            const group = getSF36Group(a.label);
                            const opts = group ? sf36Opts[group] : null;
                            const display = opts && opts[a.value] !== undefined ? opts[a.value] : a.value;
                            return <tr key={i}><td style={dtdStyle}>{labels[a.label] || a.label}</td><td style={dtdStyle}>{display}</td></tr>;
                        })}
                    </tbody>
                </table>
            );
        }
        if (qKey === 'mdasi') {
            return (
                <table style={dtStyle}>
                    <thead><tr><th style={dthStyle}>{t('patient.profile.questionnaires.question', 'Question')}</th><th style={dthStyle}>{t('patient.profile.questionnaires.answer', 'Réponse')}</th></tr></thead>
                    <tbody>
                        {answers.map((a, i) => <tr key={i}><td style={dtdStyle}>{labels[a.label] || a.label}</td><td style={dtdStyle}>{a.value} / 10</td></tr>)}
                    </tbody>
                </table>
            );
        }
        if (qKey === 'alimentation') {
            const rows = [];
            answers.forEach(a => {
                if (a.label === 'alimentation_one' && Array.isArray(a.value)) {
                    a.value.forEach((v, vi) => rows.push({ label: labels['alimentation_one_' + vi] || 'alimentation_one[' + vi + ']', value: convertYesNoMaybe(v) }));
                } else {
                    rows.push({ label: labels[a.label] || a.label, value: convertYesNoMaybe(a.value) });
                }
            });
            return (
                <table style={dtStyle}>
                    <thead><tr><th style={dthStyle}>{t('patient.profile.questionnaires.question', 'Question')}</th><th style={dthStyle}>{t('patient.profile.questionnaires.answer', 'Réponse')}</th></tr></thead>
                    <tbody>
                        {rows.map((r, i) => <tr key={i}><td style={dtdStyle}>{r.label}</td><td style={dtdStyle}>{r.value}</td></tr>)}
                    </tbody>
                </table>
            );
        }
        return null;
    };

    return (
        <>
        {/* Comment dialog */}
        {commentAlertId && (
            <div className={classes.popup_background} style={{ zIndex: 2100 }}>
                <div style={{ height: "100%", display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center" }} onClick={closeComments}>
                    <div
                        onClick={(e) => e.stopPropagation()}
                        style={{ backgroundColor: '#fff', borderRadius: 8, width: '500px', maxWidth: '90vw', maxHeight: '80vh', display: 'flex', flexDirection: 'column', boxShadow: '0 4px 20px rgba(0,0,0,0.25)' }}
                    >
                        {/* Header */}
                        <div style={{ padding: '14px 16px', borderBottom: '1px solid #eee', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <h4 style={{ margin: 0, color: '#1976d2' }}>{t('patient.profile.alert.commentsTitle', 'Commentaires')}</h4>
                            <button onClick={closeComments} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#999', lineHeight: 1 }}>x</button>
                        </div>

                        {/* Messages list */}
                        <div style={{ flex: 1, overflowY: 'auto', padding: '12px 16px', minHeight: 120 }}>
                            {loadingComments ? (
                                <p style={{ color: '#999', fontStyle: 'italic', textAlign: 'center' }}>{t('patient.profile.loading', 'Chargement...')}</p>
                            ) : comments.length === 0 ? (
                                <div style={{ textAlign: 'center', padding: '20px 0' }}>
                                    <textarea
                                        value={commentText}
                                        onChange={(e) => setCommentText(e.target.value)}
                                        placeholder={t('patient.profile.alert.commentPlaceholder', 'Ecrire un commentaire...')}
                                        style={{ width: '100%', minHeight: 60, padding: 8, fontSize: 13, border: '1px solid #ccc', borderRadius: 4, resize: 'vertical', boxSizing: 'border-box' }}
                                        onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submitComment(); } }}
                                    />
                                    <button
                                        onClick={submitComment}
                                        disabled={!commentText.trim()}
                                        style={{ marginTop: 8, padding: '8px 16px', fontSize: 13, backgroundColor: commentText.trim() ? '#1976d2' : '#ccc', color: '#fff', border: 'none', borderRadius: 4, cursor: commentText.trim() ? 'pointer' : 'default' }}
                                    >
                                        {t('button.send', 'Envoyer')}
                                    </button>
                                </div>
                            ) : (
                                comments.map((c) => {
                                    const isOwn = accountInformation?.id_user === c.FK_User;
                                    const isLocked = isLockedComment(c);
                                    const hasName = !!(c.firstName?.trim() || c.lastName?.trim());
                                    const authorName = hasName ? `${c.firstName || ''} ${c.lastName || ''}`.trim() : c.FK_User;
                                    const authorDisplay = c.structure_name?.trim() ? `${authorName} (${c.structure_name.trim()})` : authorName;
                                    const dateStr = formatAlertDate(c.created_at, dateLocale);
                                    const wasEdited = c.updated_at && c.created_at && new Date(c.updated_at).getTime() - new Date(c.created_at).getTime() > 1000;
                                    return (
                                        <div key={c.id_comment} style={{ marginBottom: 10, padding: '8px 10px', backgroundColor: isOwn ? '#e8f0fe' : '#f5f5f5', borderRadius: 6, border: '1px solid ' + (isOwn ? '#c4d8f5' : '#e0e0e0') }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                                                <span style={{ fontWeight: 600, fontSize: 12, color: '#333' }}>{authorDisplay}</span>
                                                <span style={{ fontSize: 11, color: '#888' }}>
                                                    {dateStr}{wasEdited ? ` (${t('patient.profile.alert.edited', 'modifie')})` : ''}
                                                </span>
                                            </div>
                                            {editingCommentId === c.id_comment && !isLocked ? (
                                                <div>
                                                    <textarea
                                                        autoFocus
                                                        value={editingText}
                                                        onChange={(e) => setEditingText(e.target.value)}
                                                        style={{ width: '100%', minHeight: 250, padding: 6, fontSize: 13, border: '1px solid #ccc', borderRadius: 4, resize: 'vertical', boxSizing: 'border-box' }}
                                                    />
                                                    <div style={{ display: 'flex', gap: 6, marginTop: 4, justifyContent: 'flex-end' }}>
                                                        <button onClick={() => saveEditComment(c.id_comment)} style={{ fontSize: 11, padding: '3px 10px', cursor: 'pointer', backgroundColor: '#1976d2', color: '#fff', border: 'none', borderRadius: 3 }}>
                                                            {t('button.save', 'Enregistrer')}
                                                        </button>
                                                        <button onClick={cancelEditComment} style={{ fontSize: 11, padding: '3px 10px', cursor: 'pointer', backgroundColor: '#eee', border: '1px solid #ccc', borderRadius: 3 }}>
                                                            {t('button.cancel', 'Annuler')}
                                                        </button>
                                                    </div>
                                                </div>
                                            ) : (
                                                <div>
                                                    <p style={{ margin: 0, fontSize: 13, color: '#333', whiteSpace: 'pre-wrap' }}>{c.message}</p>
                                                    {isOwn && (
                                                        isLocked ? (
                                                        <div style={{ marginTop: 5, color: '#78909c', fontSize: 11, display: 'inline-flex', alignItems: 'center', gap: 4 }} title={t('patient.profile.alert.commentLocked', 'Verrouillé après acknowledgement')}>
                                                            <span aria-hidden="true">🔒</span>
                                                            {t('patient.profile.alert.commentLocked', 'Verrouillé après acknowledgement')}
                                                        </div>
                                                        ) : (
                                                        <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                                                            <button onClick={() => startEditComment(c)} style={{ fontSize: 11, color: '#1976d2', background: 'none', border: 'none', cursor: 'pointer', padding: 0, textDecoration: 'underline' }}>
                                                                {t('button.edit', 'Modifier')}
                                                            </button>
                                                            <button onClick={() => deleteComment(c.id_comment)} style={{ fontSize: 11, color: '#DB2828', background: 'none', border: 'none', cursor: 'pointer', padding: 0, textDecoration: 'underline' }}>
                                                                {t('button.delete', 'Supprimer')}
                                                            </button>
                                                        </div>
                                                        )
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })
                            )}
                        </div>

                        {/* Input area */}
                        {comments.length > 0 && (
                        <div style={{ padding: '10px 16px', borderTop: '1px solid #eee', display: 'flex', gap: 8, alignItems: 'flex-end' }}>
                            <textarea
                                value={commentText}
                                onChange={(e) => setCommentText(e.target.value)}
                                placeholder={t('patient.profile.alert.commentPlaceholder', 'Ecrire un commentaire...')}
                                style={{ flex: 1, minHeight: 40, maxHeight: 100, padding: 8, fontSize: 13, border: '1px solid #ccc', borderRadius: 4, resize: 'vertical', boxSizing: 'border-box' }}
                                onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submitComment(); } }}
                            />
                            <button
                                onClick={submitComment}
                                disabled={!commentText.trim()}
                                style={{ padding: '8px 16px', fontSize: 13, backgroundColor: commentText.trim() ? '#1976d2' : '#ccc', color: '#fff', border: 'none', borderRadius: 4, cursor: commentText.trim() ? 'pointer' : 'default', whiteSpace: 'nowrap' }}
                            >
                                {t('button.send', 'Envoyer')}
                            </button>
                        </div>
                        )}
                    </div>
                </div>
            </div>
        )}
        {canValidateTls && showTlsConfirm && (
            <div className={classes.popup_background} style={{ zIndex: 2000 }}>
                <div style={{ height: "100%", display: "flex", flexDirection: "column", justifyContent: "center" }}>
                    <div className="submit-form" style={{ borderRadius: "5px" }}>
                        <div className="form-group" style={{ paddingTop: "5%" }}>
                            <FormattedMessage id="patient.profile.tls.confirm" defaultMessage="Êtes-vous sûr de vouloir valider la télésurveillance pour ce patient ?" />
                        </div>
                        <div style={{ display: "flex", justifyContent: "center", gap: "1em", marginTop: "0.5em" }}>
                            <button onClick={() => setShowTlsConfirm(false)} className="btn btn-danger">
                                <FormattedMessage id="button.cancel" defaultMessage="Annuler" />
                            </button>
                            <button onClick={handleTlsValidation} className="btn btn-success">
                                <FormattedMessage id="button.submit" defaultMessage="Confirmer" />
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        )}
        {showAckConfirm && (
            <div className={classes.popup_background} style={{ zIndex: 2000 }}>
                <div style={{ height: "100%", display: "flex", flexDirection: "column", justifyContent: "center" }}>
                    <div className="submit-form" style={{ borderRadius: "5px" }}>
                        <div className="form-group" style={{ paddingTop: "5%" }}>
                            <FormattedMessage id="patient.profile.alert.ackConfirm" defaultMessage="Êtes-vous sûr de vouloir valider la prise en charge de cette alerte ?" />
                        </div>
                        <div style={{ display: "flex", justifyContent: "center", gap: "1em", marginTop: "0.5em" }}>
                            <button onClick={cancelAck} className="btn btn-danger">
                                <FormattedMessage id="button.cancel" defaultMessage="Annuler" />
                            </button>
                            <button onClick={confirmAckAlert} className="btn btn-success">
                                <FormattedMessage id="button.submit" defaultMessage="Confirmer" />
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        )}
        <div className={classes.popup_background} onClick={cancelPopup}>
            <div style={{ height: "100%", display: "flex", flexDirection: "column", justifyContent: "center" }} onClick={(e) => e.stopPropagation()}>
                <div className="management-menu-popup" style={{ overflow: 'hidden', display: 'flex', flexDirection: 'column', maxWidth: isMobile ? '98%' : isCompact ? '95%' : '90%', maxHeight: isMobile ? '95vh' : '90vh', margin: '0 auto', width: isMobile ? '98%' : isCompact ? '95%' : '90%', position: 'relative' }}>
                    {/* Close button top-right */}
                    <button
                        onClick={cancelPopup}
                        style={{ position: 'absolute', top: 6, right: 20, zIndex: 10, background: '#fff', border: '1px solid #ccc', borderRadius: '50%', fontSize: 18, fontWeight: 'bold', lineHeight: 1, cursor: 'pointer', color: '#DB2828', width: 30, height: 30, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 1px 3px rgba(0,0,0,0.15)' }}
                        title={t('button.close', 'Fermer')}
                    >×</button>
                    {/* Layout toggle icons top-center — hidden below 1200px where single-column is forced */}
                    {!isCompact && (
                    <div style={{ position: 'absolute', top: 8, left: '50%', transform: 'translateX(-50%)', zIndex: 10, display: 'flex', gap: 4, background: '#fff', borderRadius: 6, border: '1px solid #ccc', padding: '2px 4px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                        <button
                            onClick={() => setTwoColLayout(false)}
                            title={t('patient.profile.layout.oneColumn', '1 colonne')}
                            style={{ background: !twoColLayout ? '#1976d2' : '#fff', color: !twoColLayout ? '#fff' : '#555', border: 'none', borderRadius: 4, cursor: 'pointer', padding: '4px 8px', display: 'flex', alignItems: 'center' }}
                        >
                            <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="2" y="2" width="14" height="14" rx="2"/></svg>
                        </button>
                        <button
                            onClick={() => setTwoColLayout(true)}
                            title={t('patient.profile.layout.twoColumns', '2 colonnes')}
                            style={{ background: twoColLayout ? '#1976d2' : '#fff', color: twoColLayout ? '#fff' : '#555', border: 'none', borderRadius: 4, cursor: 'pointer', padding: '4px 8px', display: 'flex', alignItems: 'center' }}
                        >
                            <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="2" y="2" width="6" height="14" rx="1.5"/><rect x="10" y="2" width="6" height="14" rx="1.5"/></svg>
                        </button>
                    </div>
                    )}
                    {/* Scrollable content */}
                    <div style={{ overflowY: 'auto', flex: 1, paddingTop: 10, paddingLeft: !effectiveTwoCol && !isCompact ? '10%' : isMobile ? 6 : undefined, paddingRight: !effectiveTwoCol && !isCompact ? '10%' : isMobile ? 6 : 10 }}>

                        {/* TLS validation info */}
                        {showTls && <div style={{ padding: '12px 16px', backgroundColor: '#f9fdf9', borderRadius: 6, border: '1px solid #d0e8d0', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 24, flexWrap: 'wrap' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <span style={{ color: '#888', fontSize: 12 }}>{t('patient.profile.tls.lastCheck', 'Dernier horodatage')} :</span>
                                <span style={{ fontWeight: 500, fontSize: 13 }}>
                                    {(() => {
                                        const lastEntry = (checkPatientData && checkPatientData.length > 0) ? checkPatientData[0] : null;
                                        if (!lastEntry) return '-';
                                        const dateStr = lastEntry.date || lastEntry;
                                        return formatAlertDate(dateStr, dateLocale) + (lastEntry.user_name ? ` (${lastEntry.user_name})` : '');
                                    })()}
                                </span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <span style={{ color: '#888', fontSize: 12 }}>{t('patient.profile.tls.nextDeadline', 'Prochain TLS médecin')} :</span>
                                <span style={{ fontWeight: 500, fontSize: 13 }}>
                                    {(() => {
                                        const lastEntry = (checkPatientData && checkPatientData.length > 0) ? checkPatientData[0] : null;
                                        if (!lastEntry) return '-';
                                        const dateStr = lastEntry.date || lastEntry;
                                        const deadline = new Date(new Date(dateStr).getTime() + 15 * 24 * 60 * 60 * 1000);
                                        const now = new Date();
                                        const isOverdue = deadline < now;
                                        return <span style={{ color: isOverdue ? '#f44336' : '#4caf50', fontWeight: 'bold' }}>{deadline.toLocaleDateString(dateLocale, { day: '2-digit', month: '2-digit', year: 'numeric' })}</span>;
                                    })()}
                                </span>
                            </div>
                            {canValidateTls && (
                                monitoringActive ? (
                                <button
                                    onClick={() => setShowTlsConfirm(true)}
                                    style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 14px', fontSize: 13, fontWeight: 500, backgroundColor: '#4caf50', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', transition: 'background-color 0.2s' }}
                                    title={t('patient.profile.tls.validate', 'Valider la télésurveillance')}
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
                                    {t('patient.profile.tls.validateBtn', 'Validation TLS')}
                                </button>
                                ) : (
                                <Tooltip
                                    title={<FormattedMessage id="patient.list.tlsNoActiveMonitoringTooltip" defaultMessage="TLS not possible: the patient has no active monitoring" />}
                                    placement="top"
                                >
                                    <span>
                                        <button
                                            disabled
                                            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 14px', fontSize: 13, fontWeight: 500, backgroundColor: '#DB2828', color: '#fff', border: 'none', borderRadius: 4, cursor: 'not-allowed', transition: 'background-color 0.2s' }}
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
                                            {t('patient.profile.tls.validateBtn', 'Validation TLS')}
                                        </button>
                                    </span>
                                </Tooltip>
                                )
                            )}
                        </div>}

                        {/* Top row: Patient Info + Synthesis side by side */}
                        <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', flexWrap: effectiveTwoCol ? 'nowrap' : 'wrap', marginBottom: 16 }}>

                        {/* Patient Info */}
                        <div style={{ flex: effectiveTwoCol ? '1 1 50%' : '1 1 100%', minWidth: 0, padding: isMobile ? '12px' : '16px', backgroundColor: '#fafafa', borderRadius: 6, border: '1px solid #eee' }}>
                            <div style={{ marginTop: 0, marginBottom: 14, borderBottom: '2px solid #1976d2', paddingBottom: 6, display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 6 }}>
                                <span>
                                    <span style={{ fontSize: 22, fontWeight: 700, color: '#1976d2' }}>{pd.firstname} {pd.lastname}</span>
                                    {userIsSuperAdmin(accountInformation) && !props.hidePatientIds && pd.id_patient && (
                                        <span style={{ marginLeft: 8 }}>
                                            <CopyableId value={pd.id_patient} label="id_patient" />
                                        </span>
                                    )}
                                    {pd.birthdate && <span style={{ fontSize: 14, fontWeight: 400, color: '#888', marginLeft: 10 }}>{Math.floor((new Date() - new Date(pd.birthdate)) / (365.25 * 24 * 60 * 60 * 1000))} {t('patient.profile.age', 'ans')} ({new Date(pd.birthdate).toLocaleDateString(intl ? intl.locale : 'fr-FR')})</span>}
                                </span>
                                <span style={{ fontSize: 13, fontWeight: 400, color: '#888', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                    {monitoringStartDate ? <>{pd.monitoring_activation && pd.monitoring_activation !== 'no' ? t('patient.profile.followStart', 'Début du suivi') : t('patient.profile.lastKnownFollowStart', 'Date de début du dernier suivi connu')} : {monitoringStartDate.toLocaleDateString(intl ? intl.locale : 'fr-FR')}</> : null}
                                    {monitoringStartDate ? <span style={{ color: '#bbb', margin: '0 2px' }}>-</span> : null}
                                    <span style={{ color: '#888' }}>{t('patient.profile.followLabel', 'Suivi')} :</span>
                                    <span style={{
                                        display: 'inline-block',
                                        width: 10,
                                        height: 10,
                                        borderRadius: '50%',
                                        backgroundColor: pd.monitoring_activation && pd.monitoring_activation !== 'no' ? '#4caf50' : '#f44336',
                                        boxShadow: pd.monitoring_activation && pd.monitoring_activation !== 'no' ? '0 0 6px rgba(76,175,80,0.5)' : '0 0 6px rgba(244,67,54,0.5)',
                                    }} title={pd.monitoring_activation && pd.monitoring_activation !== 'no' ? t('patient.profile.monitoringActive', 'Suivi actif') : t('patient.profile.monitoringInactive', 'Suivi inactif')} />
                                </span>
                            </div>
                            <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', fontSize: 14 }}>
                                <div style={{ flex: '1 1 30%', minWidth: 160 }}>
                                    <div style={{ color: '#888', fontSize: 12, marginBottom: 2 }}>{t('patient.profile.pathology', 'Pathologie suivie')}</div>
                                    <div style={{ fontWeight: 500 }}>{pd.concerned_speciality || '-'}</div>
                                </div>
                                <div style={{ flex: '1 1 30%', minWidth: 160 }}>
                                    <div style={{ color: '#888', fontSize: 12, marginBottom: 2 }}>{t('patient.profile.practitioner', 'Praticien')}</div>
                                    <div style={{ fontWeight: 500 }}>
                                        {practitioner
                                            ? `${practitioner.family || ''} ${practitioner.given ? practitioner.given.join(' ') : ''}`.trim()
                                            : (pd.correspondant ? `${pd.correspondant.firstName || ''} ${pd.correspondant.lastName || ''}`.trim() || pd.correspondant.username || '-' : '-')
                                        }
                                    </div>
                                    {practitioner && practitioner.msmail && (
                                        <div style={{ fontSize: 12, color: '#1976d2', marginTop: 2 }}>{practitioner.msmail}</div>
                                    )}
                                </div>
                                <div style={{ flex: '1 1 30%', minWidth: 160 }}>
                                    <div style={{ color: '#888', fontSize: 12, marginBottom: 2 }}>{t('patient.profile.location', 'Localisation')}</div>
                                    <div style={{ fontWeight: 500 }}>{pd.location || '-'}</div>
                                </div>
                            </div>
                            <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', fontSize: 14, marginTop: 12 }}>
                                <div style={{ flex: '1 1 30%', minWidth: 160 }}>
                                    <div style={{ color: '#888', fontSize: 12, marginBottom: 2 }}>{t('patient.profile.consent', 'Consentement')}</div>
                                    <div>
                                        {pd.consentement_filename ? (
                                            <span
                                                title={pd.consentement_filename}
                                                style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: '#1976d2', cursor: 'pointer' }}
                                                onClick={async () => {
                                                    try {
                                                        const data = await PatientFunctionsGetter.download_patient_consentement(pd.id_patient);
                                                        if (!data || !data.consentement_file) return;
                                                        let b64 = data.consentement_file;
                                                        if (b64.includes(',')) b64 = b64.split(',')[1];
                                                        const byteCharacters = atob(b64);
                                                        const byteNumbers = new Array(byteCharacters.length);
                                                        for (let i = 0; i < byteCharacters.length; i++) {
                                                            byteNumbers[i] = byteCharacters.charCodeAt(i);
                                                        }
                                                        const blob = new Blob([new Uint8Array(byteNumbers)], { type: 'application/pdf' });
                                                        const url = window.URL.createObjectURL(blob);
                                                        const a = document.createElement('a');
                                                        a.href = url;
                                                        a.download = data.consentement_filename || 'consentement';
                                                        document.body.appendChild(a);
                                                        a.click();
                                                        a.remove();
                                                        window.URL.revokeObjectURL(url);
                                                    } catch (e) {
                                                        console.error('Download consentement error:', e);
                                                    }
                                                }}
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#1976d2" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                                                    <polyline points="14 2 14 8 20 8"/>
                                                    <line x1="16" y1="13" x2="8" y2="13"/>
                                                    <line x1="16" y1="17" x2="8" y2="17"/>
                                                    <polyline points="10 9 9 9 8 9"/>
                                                </svg>
                                                <span style={{ fontSize: 13 }}>{pd.consentement_filename}</span>
                                            </span>
                                        ) : <span style={{ color: '#999' }}>-</span>}
                                    </div>
                                </div>
                                <div style={{ flex: '1 1 30%', minWidth: 160 }}>
                                    <div style={{ color: '#888', fontSize: 12, marginBottom: 2 }}>{t('patient.profile.ordonnance', 'Ordonnance')}</div>
                                    <div>
                                        {pd.ordonnance_filename ? (
                                            <span
                                                title={pd.ordonnance_filename}
                                                style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: '#1976d2', cursor: 'pointer' }}
                                                onClick={async () => {
                                                    try {
                                                        const data = await PatientFunctionsGetter.download_patient_ordonnance(pd.id_patient);
                                                        if (!data || !data.ordonnance_file) return;
                                                        let b64 = data.ordonnance_file;
                                                        if (b64.includes(',')) b64 = b64.split(',')[1];
                                                        const byteCharacters = atob(b64);
                                                        const byteNumbers = new Array(byteCharacters.length);
                                                        for (let i = 0; i < byteCharacters.length; i++) {
                                                            byteNumbers[i] = byteCharacters.charCodeAt(i);
                                                        }
                                                        const blob = new Blob([new Uint8Array(byteNumbers)], { type: 'application/pdf' });
                                                        const url = window.URL.createObjectURL(blob);
                                                        const a = document.createElement('a');
                                                        a.href = url;
                                                        a.download = data.ordonnance_filename || 'ordonnance';
                                                        document.body.appendChild(a);
                                                        a.click();
                                                        a.remove();
                                                        window.URL.revokeObjectURL(url);
                                                    } catch (e) {
                                                        console.error('Download ordonnance error:', e);
                                                    }
                                                }}
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#1976d2" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                                                    <polyline points="14 2 14 8 20 8"/>
                                                    <line x1="16" y1="13" x2="8" y2="13"/>
                                                    <line x1="16" y1="17" x2="8" y2="17"/>
                                                    <polyline points="10 9 9 9 8 9"/>
                                                </svg>
                                                <span style={{ fontSize: 13 }}>{pd.ordonnance_filename}</span>
                                            </span>
                                        ) : <span style={{ color: '#999' }}>-</span>}
                                    </div>
                                </div>
                                <div style={{ flex: '1 1 30%', minWidth: 160 }}>
                                    <div style={{ color: '#888', fontSize: 12, marginBottom: 2 }}>{t('patient.profile.psad', 'PSAD')}</div>
                                    <div style={{ marginTop: 2, marginBottom: 4 }}>
                                        <span style={{ color: '#888', fontSize: 12 }}>{t('patient.profile.psad_structure', 'Structure')} : </span>
                                        <span style={{ fontWeight: 500, fontSize: 12 }}>{psadShareStruct?.structure_name || psadStructureName || pd.psad_delegate || '-'}</span>
                                    </div>
                                    <div style={{ fontWeight: 500, fontSize: 12 }}>
                                        {psadShareStruct?.address || '-'}
                                    </div>
                                    <div style={{ marginTop: 2 }}>
                                        <span style={{ color: '#888', fontSize: 12 }}>{t('patient.profile.psad_mail', 'Mail PSAD')} : </span>
                                        <span style={{ fontWeight: 500, fontSize: 12 }}>{psadShareStruct?.email || '-'}</span>
                                    </div>
                                    <div style={{ color: '#888', fontSize: 12, marginBottom: 2, marginTop: 8 }}>{t('patient.profile.owner', 'Owner')}</div>
                                    <div style={{ fontWeight: 500 }}>{pd.FK_User_mail ? (pd.FK_User_mail + (pd.structure_name ? ' (' + pd.structure_name + ')' : '')) : '-'}</div>
                                </div>
                            </div>
                        </div>{/* End Patient Info */}

                        {/* Synthesis (right side in two-col, below in single) */}
                        {monitoringSynthesis.length > 0 && (
                            <div
                                style={{ flex: effectiveTwoCol ? '1 1 50%' : '1 1 100%', minWidth: 0, padding: isMobile ? '12px' : '16px', backgroundColor: '#f5f8ff', borderRadius: 6, border: '1px solid #d0d8f0', cursor: 'pointer', userSelect: 'none' }}
                                onClick={() => setSynthesisExpanded(!synthesisExpanded)}
                            >
                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                    <h3 style={{ margin: 0, color: '#1976d2', flex: 1 }}>{t('patient.profile.synthesis', 'Synthèse télésurveillance')}</h3>
                                    <span style={{ fontSize: 16, color: '#1976d2', marginLeft: 8 }}>{synthesisExpanded ? '▼' : '▶'}</span>
                                </div>
                                {synthesisExpanded && (() => {
                                    const totalAllAlerts = monitoringSynthesis.reduce((sum, row) => sum + row.totalAlerts, 0);
                                    const half = Math.ceil(monitoringSynthesis.length / 2);
                                    const leftRows = monitoringSynthesis.slice(0, half);
                                    const rightRows = monitoringSynthesis.slice(half);
                                    const thStyle = { padding: '6px 8px', textAlign: 'center', fontSize: 12 };
                                    const thLeftStyle = { ...thStyle, textAlign: 'left' };
                                    const tdStyle = { padding: '5px 8px', fontSize: 13 };
                                    const renderTable = (rows) => (
                                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                            <thead>
                                                <tr style={{ backgroundColor: '#e8eef8', borderBottom: '2px solid #c0c8e0' }}>
                                                    <th style={thLeftStyle}>{t('patient.profile.synthesis.constant', 'Constante')}</th>
                                                    <th style={thStyle}>{t('patient.profile.synthesis.alerts', 'Alertes')}</th>
                                                    <th style={thStyle}>{t('patient.profile.synthesis.lastAlert', 'Dernière')}</th>
                                                    <th style={thStyle}>{t('patient.profile.synthesis.exceedance', 'Dépass. moy.')}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {rows.map((row, i) => (
                                                    <tr key={i} style={{ borderBottom: '1px solid #e0e4f0' }}>
                                                        <td style={{ ...tdStyle, fontWeight: 500 }}>{row.label}</td>
                                                        <td style={{ ...tdStyle, textAlign: 'center', color: row.totalAlerts > 0 ? '#DB2828' : undefined, fontWeight: row.totalAlerts > 0 ? 'bold' : 'normal' }}>{row.totalAlerts}</td>
                                                        <td style={{ ...tdStyle, textAlign: 'center' }}>{row.lastAlert ? formatAlertDate(row.lastAlert, dateLocale) : '-'}</td>
                                                        <td style={{ ...tdStyle, textAlign: 'center' }}>{row.avgExceedance != null ? `+${row.avgExceedance.toFixed(1)}%` : '-'}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    );
                                    // Mobile: stacked blocks per constant, same style as the compact alert lists
                                    const renderCompactRows = () => (
                                        <div>
                                            {monitoringSynthesis.map((row, i) => (
                                                <div key={i} style={{ border: '1px solid #d0d8f0', borderRadius: 6, padding: '8px 10px', marginBottom: 8, backgroundColor: '#fff' }}>
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 4 }}>
                                                        <span style={{ fontWeight: 600, fontSize: 13 }}>{row.label}</span>
                                                        <span style={{ fontSize: 13, color: row.totalAlerts > 0 ? '#DB2828' : '#767676', fontWeight: row.totalAlerts > 0 ? 'bold' : 'normal' }}>
                                                            {row.totalAlerts > 0 ? `⚠ ${row.totalAlerts}` : '0'} {t('patient.profile.synthesis.alerts', 'Alertes').toLowerCase()}
                                                        </span>
                                                    </div>
                                                    <div style={{ display: 'flex', gap: 16, fontSize: 12, marginTop: 4, flexWrap: 'wrap' }}>
                                                        <span><span style={{ color: '#888' }}>{t('patient.profile.synthesis.lastAlert', 'Dernière')} :</span> <strong>{row.lastAlert ? formatAlertDate(row.lastAlert, dateLocale) : '-'}</strong></span>
                                                        <span><span style={{ color: '#888' }}>{t('patient.profile.synthesis.exceedance', 'Dépass. moy.')} :</span> <strong>{row.avgExceedance != null ? `+${row.avgExceedance.toFixed(1)}%` : '-'}</strong></span>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    );
                                    return (
                                        <div style={{ marginTop: 14 }} onClick={e => e.stopPropagation()}>
                                            <div style={{ marginBottom: 10, padding: '8px 12px', backgroundColor: '#e8eef8', borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, flexWrap: 'wrap', textAlign: 'center' }}>
                                                <span style={{ fontSize: 13, color: '#333' }}>{t('patient.profile.synthesis.totalAlerts', 'Nombre total d\'alertes (toutes constantes)')}</span>
                                                <span style={{ fontSize: 15, fontWeight: 'bold', color: totalAllAlerts > 0 ? '#DB2828' : '#333' }}>{totalAllAlerts}</span>
                                            </div>
                                            {isMobile ? renderCompactRows() : (
                                                <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                                                    <div style={{ flex: '1 1 280px', minWidth: 0 }}>{renderTable(leftRows)}</div>
                                                    {rightRows.length > 0 && <div style={{ flex: '1 1 280px', minWidth: 0 }}>{renderTable(rightRows)}</div>}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })()}
                            </div>
                        )}

                        </div>{/* End top row: Patient Info + Synthesis */}

                        {/* Content sections — two columns or single column */}
                        <div style={{ display: effectiveTwoCol ? 'flex' : 'block', gap: 16, alignItems: 'flex-start' }}>

                        {/* Left column (or full width): Active Alerts + Past Alerts */}
                        <div style={{ flex: 1, minWidth: 0 }}>

                        {/* Section 3: Current alerts list */}
                        {pd.monitoring_activation && pd.monitoring_activation !== 'no' ? (
                            <div style={{ marginBottom: 24, padding: '16px', backgroundColor: '#fff8f8', borderRadius: 6, border: '1px solid #f0d0d0' }}>
                                <AlertTable
                                    alerts={sortedActiveAlerts}
                                    title={t('patient.profile.activeAlerts', 'Alertes en cours') + ' (' + activeAlerts.length + ')'}
                                    emptyMessage={t('patient.profile.noActiveAlerts', 'Aucune alerte en cours')}
                                    intl={intl}
                                    alertConfigs={pd.alerts}
                                    onAck={requestAckAlert}
                                    pendingAckId={pendingAckId}
                                    onActionChange={handleActionChange}
                                    alertActions={alertActions}
                                    disableAck={practitionerHasDelegatedAlerts}
                                    commentCounts={commentCounts}
                                    commentPreviews={commentPreviews}
                                    onOpenComments={openComments}
                                    currentUserId={accountInformation?.id_user}
                                    onEditFirstComment={openCommentsAndEditFirst}
                                    compact={isAlertCompact}
                                />
                            </div>
                        ) : (
                            <div style={{ marginBottom: 24, padding: '16px', backgroundColor: '#f5f5f5', borderRadius: 6, border: '1px solid #e0e0e0' }}>
                                <p style={{ margin: 0, color: '#999', fontStyle: 'italic' }}>{t('patient.profile.monitoringDisabled', "Alertes temps réel non disponibles — le monitoring n'est pas activé pour ce patient.")}</p>
                            </div>
                        )}

                        {/* Section 5: Past alerts list */}
                        <div style={{ marginBottom: 24, padding: '16px', backgroundColor: '#fafafa', borderRadius: 6, border: '1px solid #eee' }}>
                            <h3 style={{ margin: '0 0 12px', color: '#333' }}>{t('patient.profile.pastAlerts', 'Alertes passées')} ({sortedPassedAlerts.length}/{passedAlerts.length})</h3>

                            {passedAlerts.length > 0 && (
                                <div style={{ marginBottom: 12, display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
                                    {/* Type filter tags */}
                                    <span style={{ fontSize: 12, color: '#888', marginRight: 2 }}>{t('patient.profile.pastAlerts.filterType', 'Filtrer')} :</span>
                                    {pastAlertTypes.map(type => {
                                        const isActive = pastAlertTypeFilter.has(type);
                                        return (
                                            <button
                                                key={type}
                                                onClick={() => {
                                                    setPastAlertTypeFilter(prev => {
                                                        const next = new Set(prev);
                                                        if (next.has(type)) next.delete(type); else next.add(type);
                                                        return next;
                                                    });
                                                }}
                                                style={{
                                                    padding: '3px 10px', fontSize: 11, borderRadius: 12, cursor: 'pointer',
                                                    border: isActive ? '1px solid #1976d2' : '1px solid #ccc',
                                                    backgroundColor: isActive ? '#1976d2' : '#fff',
                                                    color: isActive ? '#fff' : '#555',
                                                    fontWeight: isActive ? 600 : 400,
                                                    transition: 'all 0.15s',
                                                }}
                                            >
                                                {alertTypeLabel(t, type)}
                                            </button>
                                        );
                                    })}
                                    {pastAlertTypeFilter.size > 0 && (
                                        <button
                                            onClick={() => setPastAlertTypeFilter(new Set())}
                                            style={{ padding: '3px 8px', fontSize: 11, borderRadius: 12, cursor: 'pointer', border: '1px solid #f44336', backgroundColor: '#fff', color: '#f44336' }}
                                        >✕ {t('patient.profile.pastAlerts.clearFilter', 'Tout')}</button>
                                    )}
                                    {/* Separator */}
                                    <span style={{ borderLeft: '1px solid #ddd', height: 20, margin: '0 4px' }} />
                                    {/* Fixed descending acknowledgement sort + alert-date range */}
                                    <span
                                        style={{ padding: '4px 10px', fontSize: 12, borderRadius: 4, border: '1px solid #ccc', backgroundColor: '#f7f7f7', color: '#555', display: 'flex', alignItems: 'center', gap: 4 }}
                                        title={t('patient.profile.pastAlerts.sortAckDesc', 'Acknowledgement du plus récent au plus ancien')}
                                    >
                                        {t('patient.profile.alert.validation', 'Acknowledgement')} ↓
                                    </span>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                        <span style={{ fontSize: 12, color: '#888' }}>{t('patient.profile.pastAlerts.from', 'Du')} :</span>
                                        <input
                                            type="date"
                                            value={pastAlertDateFrom}
                                            onChange={e => setPastAlertDateFrom(e.target.value)}
                                            style={{ fontSize: 12, padding: '3px 6px', borderRadius: 4, border: '1px solid #ccc' }}
                                        />
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                        <span style={{ fontSize: 12, color: '#888' }}>{t('patient.profile.pastAlerts.to', 'Au')} :</span>
                                        <input
                                            type="date"
                                            value={pastAlertDateTo}
                                            onChange={e => setPastAlertDateTo(e.target.value)}
                                            style={{ fontSize: 12, padding: '3px 6px', borderRadius: 4, border: '1px solid #ccc' }}
                                        />
                                    </div>
                                    {(pastAlertDateFrom || pastAlertDateTo) && (
                                        <button
                                            onClick={() => { setPastAlertDateFrom(''); setPastAlertDateTo(''); }}
                                            style={{ padding: '3px 8px', fontSize: 11, borderRadius: 4, cursor: 'pointer', border: '1px solid #f44336', backgroundColor: '#fff', color: '#f44336' }}
                                        >✕ {t('patient.profile.pastAlerts.clearDates', 'Dates')}</button>
                                    )}
                                </div>
                            )}

                            <AlertTable
                                alerts={sortedPassedAlerts}
                                title={null}
                                emptyMessage={t('patient.profile.noPastAlerts', 'Aucune alerte passée')}
                                intl={intl}
                                alertConfigs={pd.alerts}
                                readOnly={true}
                                // A past alert is acknowledged: its comments are locked. readOnly
                                // alone does not gate them — the "add a comment" affordance keys on
                                // noNewComments (TWAPP032 step 7, .tec expected result 7: "the
                                // comment written before the acknowledgement must be shown as
                                // locked and must not be modifiable").
                                noNewComments={true}
                                commentCounts={commentCounts}
                                commentPreviews={commentPreviews}
                                onOpenComments={openComments}
                                currentUserId={accountInformation?.id_user}
                                onEditFirstComment={openCommentsAndEditFirst}
                                compact={isAlertCompact}
                            />
                        </div>

                        </div>{/* End left column */}

                        {/* Right column: Graphs + Questionnaires */}
                        <div style={{ flex: 1, minWidth: 0 }}>

                        {/* Section 3: Graphs (foldable) */}
                        <div
                            style={{ marginBottom: 24, padding: '16px', backgroundColor: '#f5f8ff', borderRadius: 6, border: '1px solid #d0d8f0', cursor: 'pointer', userSelect: 'none' }}
                            onClick={() => setGraphsExpanded(!graphsExpanded)}
                        >
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#1976d2" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 8, flexShrink: 0 }}>
                                    <line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/>
                                </svg>
                                <h3 style={{ margin: 0, color: '#1976d2', flex: 1 }}>
                                    {t('patient.profile.graphs.title', 'Graphiques')} ({availableGraphs.length} {availableGraphs.length > 1 ? t('patient.profile.graphs.availables', 'disponibles') : t('patient.profile.graphs.available', 'disponible')})
                                </h3>
                                <span style={{ fontSize: 16, color: '#1976d2', marginLeft: 8 }}>{graphsExpanded ? '▼' : '▶'}</span>
                            </div>
                            {graphsExpanded && (
                                <div style={{ marginTop: 16 }} onClick={e => e.stopPropagation()}>
                                    {availableGraphs.length === 0 ? (
                                        <p style={{ color: '#999', fontStyle: 'italic' }}>{t('patient.profile.graphs.none', 'Aucun graphique disponible pour ce patient')}</p>
                                    ) : (
                                        <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                                            <span style={{ color: '#555', fontSize: 13, fontWeight: 500, whiteSpace: 'nowrap' }}>{t('home.dates', 'Dates')} :</span>
                                            <DatePeriod getDate={handleGraphDateChange} screenSize={isMobile ? screenSize : { width: 800 }} />
                                        </div>
                                    )}
                                </div>
                            )}
                            {graphsExpanded && !zoomedGraph && (
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginTop: availableGraphs.length > 0 ? 16 : 0 }} onClick={e => e.stopPropagation()}>
                                    {availableGraphs.map(g => {
                                        const graphBasis = (isMobile || availableGraphs.length <= 3) ? '100%' : 'calc(50% - 6px)';
                                        return (
                                            <div key={g.key} style={{ flex: `1 1 ${graphBasis}`, minWidth: 280, maxWidth: graphBasis, marginBottom: 12, padding: 12, backgroundColor: '#fff', borderRadius: 4, border: '1px solid #e0e0e0', overflow: 'hidden' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '0 0 8px' }}>
                                                    <h4 style={{ margin: 0, color: '#555' }}>{g.label}</h4>
                                                    <button
                                                        onClick={(e) => { e.stopPropagation(); setZoomedGraph(g.key); }}
                                                        title={t('patient.profile.graphs.enlarge', 'Agrandir')}
                                                        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, borderRadius: 4, display: 'flex', alignItems: 'center', transition: 'background 0.2s' }}
                                                        onMouseEnter={e => e.currentTarget.style.background = '#e3f2fd'}
                                                        onMouseLeave={e => e.currentTarget.style.background = 'none'}
                                                    >
                                                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#1976d2" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                            <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/><line x1="11" y1="8" x2="11" y2="14"/><line x1="8" y1="11" x2="14" y2="11"/>
                                                        </svg>
                                                    </button>
                                                </div>
                                                {g.render()}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                            {/* Zoom modal: small graphs are unmounted (freeing canvas IDs), zoomed graph re-renders with same IDs */}
                            {zoomedGraph && (() => {
                                const zg = availableGraphs.find(g => g.key === zoomedGraph);
                                if (!zg) return null;
                                return (
                                    <div
                                        onClick={(e) => { e.stopPropagation(); setZoomedGraph(null); }}
                                        style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                    >
                                        <div
                                            onClick={e => e.stopPropagation()}
                                            style={{ backgroundColor: '#fff', borderRadius: 8, padding: 24, width: '85vw', maxWidth: 1400, maxHeight: '90vh', overflow: 'auto', boxShadow: '0 8px 32px rgba(0,0,0,0.25)', position: 'relative' }}
                                        >
                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                                                <h3 style={{ margin: 0, color: '#1976d2' }}>{zg.label}</h3>
                                                <button
                                                    onClick={() => setZoomedGraph(null)}
                                                    style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 22, color: '#888', padding: '4px 8px', borderRadius: 4, lineHeight: 1 }}
                                                    onMouseEnter={e => e.currentTarget.style.color = '#333'}
                                                    onMouseLeave={e => e.currentTarget.style.color = '#888'}
                                                    title={t('button.close', 'Fermer')}
                                                >✕</button>
                                            </div>
                                            <div style={{ position: 'relative', width: '100%', height: '65vh' }}>
                                                {zg.render(6)}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })()}
                        </div>

                        {/* Section 4: Questionnaires (interactive accordion) */}
                        <div
                            style={{ marginBottom: 24, padding: '16px', backgroundColor: '#f5f8ff', borderRadius: 6, border: '1px solid #d0d8f0', cursor: 'pointer', userSelect: 'none' }}
                            onClick={() => setQuestionnairesExpanded(!questionnairesExpanded)}
                        >
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#1976d2" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 8, flexShrink: 0 }}>
                                    <path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/>
                                </svg>
                                <h3 style={{ margin: 0, color: '#1976d2', flex: 1 }}>
                                    {t('patient.profile.questionnaires.title', 'Questionnaires')} ({availableQuestionnaires.length} {availableQuestionnaires.length > 1 ? t('patient.profile.graphs.availables', 'disponibles') : t('patient.profile.graphs.available', 'disponible')})
                                </h3>
                                <span style={{ fontSize: 16, color: '#1976d2', marginLeft: 8 }}>{questionnairesExpanded ? '▼' : '▶'}</span>
                            </div>
                            {questionnairesExpanded && availableQuestionnaires.length === 0 && (
                                <div style={{ marginTop: 16 }}>
                                    <p style={{ color: '#999', fontStyle: 'italic' }}>{t('patient.profile.questionnaires.none', 'Aucun questionnaire disponible pour ce patient')}</p>
                                </div>
                            )}
                            <div style={{ display: questionnairesExpanded ? 'block' : 'none', marginTop: 16 }} onClick={e => e.stopPropagation()}>
                                {availableQuestionnaires.map(q => {
                                    const isQExpanded = expandedQuestionnaires.has(q.key);
                                    const responses = isQExpanded ? getFormRes(q.key) : [];
                                    const page = questionnairePage[q.key] || 0;
                                    const totalPages = Math.ceil(responses.length / ITEMS_PER_PAGE);
                                    const pagedResponses = responses.slice(page * ITEMS_PER_PAGE, (page + 1) * ITEMS_PER_PAGE);
                                    const totalCount = getFormRes(q.key).length;
                                    return (
                                        <div key={q.key} style={{ marginBottom: 12, backgroundColor: '#fff', borderRadius: 4, border: '1px solid #e0e0e0' }}>
                                            <div
                                                onClick={() => toggleQuestionnaire(q.key)}
                                                style={{ padding: '10px 12px', cursor: 'pointer', display: 'flex', alignItems: 'center', userSelect: 'none', backgroundColor: isQExpanded ? '#e8eef8' : '#fff', borderRadius: isQExpanded ? '4px 4px 0 0' : 4 }}
                                            >
                                                <span style={{ marginRight: 8 }}>{isQExpanded ? '▼' : '▶'}</span>
                                                <h4 style={{ margin: 0, color: '#1976d2' }}>{q.label}</h4>
                                                <span style={{ marginLeft: 'auto', color: '#999', fontSize: 12 }}>
                                                    {totalCount} {totalCount > 1 ? t('patient.profile.questionnaires.responses', 'réponses') : t('patient.profile.questionnaires.response', 'réponse')}
                                                </span>
                                            </div>
                                            {isQExpanded && (
                                                <div style={{ padding: '0 12px 12px' }}>
                                                    {pagedResponses.map((response, idx) => {
                                                        const globalIdx = q.key + '_' + (page * ITEMS_PER_PAGE + idx);
                                                        const isRespExpanded = expandedResponseIdx === globalIdx;
                                                        return (
                                                            <div key={globalIdx} style={{ borderBottom: '1px solid #eee' }}>
                                                                <div
                                                                    onClick={() => setExpandedResponseIdx(isRespExpanded ? null : globalIdx)}
                                                                    style={{ padding: '8px 0', cursor: 'pointer', display: 'flex', alignItems: 'center', userSelect: 'none' }}
                                                                >
                                                                    <span style={{ marginRight: 8, fontSize: 12 }}>{isRespExpanded ? '▾' : '▸'}</span>
                                                                    <span style={{ color: '#1976d2', fontWeight: 500 }}>{formatQDate(response.x)}</span>
                                                                </div>
                                                                {isRespExpanded && (
                                                                    <div style={{ padding: '4px 0 12px 20px' }}>
                                                                        {renderResponseDetail(q.key, response)}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        );
                                                    })}
                                                    {totalPages > 1 && (
                                                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginTop: 8, gap: 8 }}>
                                                            <button
                                                                onClick={() => { setQuestionnairePage(prev => ({ ...prev, [q.key]: Math.max(0, page - 1) })); setExpandedResponseIdx(null); }}
                                                                disabled={page === 0}
                                                                style={{ padding: '4px 12px', cursor: page === 0 ? 'default' : 'pointer', opacity: page === 0 ? 0.4 : 1, border: '1px solid #ccc', borderRadius: 3, backgroundColor: '#fff' }}
                                                            >
                                                                ←
                                                            </button>
                                                            <span style={{ fontSize: 13, color: '#666' }}>{page + 1} / {totalPages}</span>
                                                            <button
                                                                onClick={() => { setQuestionnairePage(prev => ({ ...prev, [q.key]: Math.min(totalPages - 1, page + 1) })); setExpandedResponseIdx(null); }}
                                                                disabled={page >= totalPages - 1}
                                                                style={{ padding: '4px 12px', cursor: page >= totalPages - 1 ? 'default' : 'pointer', opacity: page >= totalPages - 1 ? 0.4 : 1, border: '1px solid #ccc', borderRadius: 3, backgroundColor: '#fff' }}
                                                            >
                                                                →
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        </div>{/* End right column */}
                        </div>{/* End two-column layout */}
                    </div>

                    {/* Close button */}
                    <Row style={{ flexShrink: 0, paddingTop: 10 }}>
                        <center>
                            <button onClick={cancelPopup} className="btn btn-danger">
                                <FormattedMessage id="button.close" defaultMessage="Fermer" />
                            </button>
                        </center>
                    </Row>
                </div>
            </div>
        </div>
        </>
    );
};

export default withStyles(Constants.styles)(PatientProfileModal);
